import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSyncContext } from '../contexts/SyncContext';
import aiAutomationApi from '../utils/aiAutomationApi';
import Toast from './Toast';
import { ProcessLogsModal, ProcessStatusCard } from './ProcessLogSummary';
import { getCid, getMetadata } from '../utils/encryptedStorage';
import { RESUME_LIBRARY_LABEL, getSourceTypeLabel } from '../utils/uxLabels';
import {
  cancelGmailAccountSwitch,
  confirmGmailAccountSwitch,
  handleMailProviderReconnect,
  isMailProviderAuthError,
  openMailOAuthUrlPopup,
} from '../utils/gmailOAuth';
import ScoreDetailsInfo from './ScoreDetailsInfo';
import JDScoreConfigModal from './JDScoreConfigModal';
import {
  findWorkflowSnapshot,
  workflowCount,
  workflowStateToLegacyStatus,
  workflowSyntheticLog,
} from '../utils/workflowSnapshot';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AI_SSE_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://127.0.0.1:8100/aibot/v1';

const hexToRgba = (hex, alpha) => {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return hex;
  const r = parseInt(safe.slice(0, 2), 16);
  const g = parseInt(safe.slice(2, 4), 16);
  const b = parseInt(safe.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const pluralLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const buildSyncCompleteToast = (stats = {}, unparsedCount = 0) => {
  const totalMails = Number(stats.totalMails || 0);
  const parsedResumes = Number(stats.parsedResumes || 0);
  const duplicateResumes = Number(stats.duplicateResumes || 0);
  const unparsedResumes = Number(unparsedCount || 0);
  const parts = [
    'Sync completed',
    `${pluralLabel(totalMails, 'mail')} extracted`,
    `${pluralLabel(parsedResumes, 'resume')} parsed`,
  ];
  if (duplicateResumes > 0) parts.push(`${pluralLabel(duplicateResumes, 'duplicate resume', 'duplicate resumes')}`);
  if (unparsedResumes > 0) parts.push(`${pluralLabel(unparsedResumes, 'resume')} not parsed`);
  return parts.join(' • ');
};

const normalizeSourcingChannel = (rawChannel) => {
  if (!rawChannel || typeof rawChannel !== 'string') return null;
  const normalized = rawChannel.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('linkedin')) return 'linkedin';
  if (normalized.includes('google') || normalized.includes('online') || normalized === 'search' || normalized.includes('searxng')) return 'google';
  if (normalized.includes('resumebox') || normalized.includes('resume box') || normalized.includes('resume_box') || normalized.includes('resume')) return 'resumebox';
  return null;
};

const SourceBadge = ({ source }) => {
  const label = (source || '').toString().toLowerCase();
  if (label.includes('google') || label.includes('online') || label.includes('web')) {
    return (
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', border: '1px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 8, fontWeight: 700, background: 'conic-gradient(from 180deg at 50% 50%, #4285F4 0deg, #4285F4 90deg, #34A853 90deg, #34A853 180deg, #FBBC05 180deg, #FBBC05 270deg, #EA4335 270deg)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>G</span>
      </div>
    );
  }
  if (label.includes('linkedin') || label === 'in') {
    return <div style={{ width: 16, height: 16, borderRadius: 3, background: '#0A66C2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>in</span></div>;
  }
  if (label.includes('naukri')) {
    return <div style={{ width: 16, height: 16, borderRadius: 3, background: '#0B5ED7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>N</span></div>;
  }
  if (label.includes('resume') || label === RESUME_LIBRARY_LABEL.toLowerCase()) {
    return <div style={{ width: 16, height: 16, borderRadius: 3, background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>R</span></div>;
  }
  if (label.includes('job_post') || label.includes('job post')) {
    return <div style={{ width: 16, height: 16, borderRadius: 3, background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>J</span></div>;
  }
  return null;
};

const SOURCE_FILTER_OPTIONS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'google', label: 'Online Sourcing' },
  { value: 'resume_box', label: RESUME_LIBRARY_LABEL },
  { value: 'naukri', label: 'Naukri' },
  { value: 'job_post', label: 'Job Post' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'imported', label: 'Imported' },
  { value: 'identified', label: 'Sourced' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'hired', label: 'Hired' },
];

const normalizeFilterToken = (value) => (value || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const getCandidateSourceKeys = (candidate) => {
  const values = [
    candidate.source,
    candidate.source_type,
    candidate._source_type,
    candidate.platform,
    candidate.platforms,
    candidate.origin,
    ...getFoundOnLabels(candidate),
  ];
  const tokens = values.flatMap((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((item) => normalizeFilterToken(item));
    return [normalizeFilterToken(value)];
  });

  const keys = new Set();
  tokens.forEach((token) => {
    if (!token) return;
    if (token.includes('linkedin') || token === 'in') keys.add('linkedin');
    if (token.includes('naukri')) keys.add('naukri');
    if (token.includes('google') || token.includes('online') || token.includes('web')) keys.add('google');
    if (token.includes('resume')) keys.add('resume_box');
    if (token.includes('job_post') || token.includes('jobposting')) keys.add('job_post');
  });
  return Array.from(keys);
};

const getCandidateStatusKey = (candidate) => {
  const rawStatus = candidate.funnel_stage || candidate.stage || candidate.status || candidate.candidate_status || candidate.pipeline_status || 'Sourced';
  const token = normalizeFilterToken(rawStatus);
  // Imported candidates at the initial sourced stage are their own "Imported" status.
  const isImported = normalizeFilterToken(candidate.source_type || candidate._source_type || '') === 'imported';
  if (isImported && (token.includes('identified') || token.includes('sourced') || !token)) return 'imported';
  if (token.includes('identified') || token.includes('sourced')) return 'identified';
  if (token.includes('interview')) return 'interviewed';
  if (token.includes('schedule')) return 'scheduled';
  if (token.includes('follow')) return 'follow_up';
  if (token.includes('declined') || token.includes('rejected')) return 'declined';
  return token || 'identified';
};

const getCandidateSearchText = (candidate) => {
  const skills = candidate.skills || candidate.key_skills || [];
  const skillText = Array.isArray(skills)
    ? skills.map((skill) => (typeof skill === 'string' ? skill : skill?.name || skill?.label || '')).join(' ')
    : skills?.toString?.() || '';
  return [
    candidate.name,
    candidate.full_name,
    candidate.title,
    candidate.headline,
    candidate.current_position,
    candidate.current_role,
    candidate.company,
    candidate.current_company,
    candidate.location,
    candidate.city,
    candidate.email,
    skillText,
  ].filter(Boolean).join(' ').toLowerCase();
};

const candidateMatchesFilters = (candidate, searchTerm, sourceFilter, statusFilter) => {
  // Rejected (recruiter) and declined (candidate) never appear in the sourcing view — they live
  // only in the Overview's Rejected panel. (getCandidateStatusKey maps both to 'declined'.)
  if (getCandidateStatusKey(candidate) === 'declined') return false;

  const query = (searchTerm || '').trim().toLowerCase();
  if (query && !getCandidateSearchText(candidate).includes(query)) return false;

  if (sourceFilter.length > 0) {
    const sourceKeys = getCandidateSourceKeys(candidate);
    if (!sourceFilter.some((source) => sourceKeys.includes(source))) return false;
  }

  if (statusFilter.length > 0) {
    const statusKey = getCandidateStatusKey(candidate);
    if (!statusFilter.includes(statusKey)) return false;
  }

  return true;
};

const getFoundOnLabels = (candidate) => {
  const labels = [];
  const sourceType = (candidate.source_type || candidate._source_type || '').toString().toLowerCase();
  const platform = (candidate.platforms || '').toString().toLowerCase();
  const directLabel = getSourceTypeLabel(sourceType, platform);
  if (directLabel) {
    labels.push(directLabel);
  } else {
    const platforms = Array.isArray(candidate.platforms) ? candidate.platforms : typeof candidate.platforms === 'string' ? [candidate.platforms] : [];
    platforms.forEach((p) => {
      const val = (p || '').toString().toLowerCase();
      if (val.includes('google') && !labels.includes('Online Sourcing')) labels.push('Online Sourcing');
      if ((val.includes('linkedin') || val === 'in') && !labels.includes('LinkedIn')) labels.push('LinkedIn');
      if (val.includes('naukri') && !labels.includes('Naukri')) labels.push('Naukri');
    });
  }
  return labels;
};

const extractCandidate = (candidate, index) => {
  const candidateId = candidate.id || candidate.candidate_id || `candidate_${index}`;
  const name = (candidate.name || candidate.full_name || 'Unknown').trim();
  const initials = name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const title = candidate.title || candidate.headline || candidate.current_position || candidate.current_role || candidate.role || 'N/A';
  const company = candidate.company || candidate.current_company || 'N/A';
  const location = candidate.location || candidate.city || 'N/A';
  const email = candidate.email || candidate.email_id || null;
  const phone = candidate.phone || null;
  const matchScore = candidate.match_percentage || candidate.matchPercentage || candidate.match_score || candidate.overall_match || candidate.score || 0;
  const scoreDetails = candidate.score_details || candidate.scoreDetails || {};
  const linkedinUrl = candidate.linkedin_profile_url || candidate.linkedin_url || null;
  const experienceYearsValue =
    candidate.experience_years ||
    candidate.total_experience_years ||
    candidate.total_experience ||
    candidate.years_experience ||
    null;

  let experience = experienceYearsValue || candidate.experience || 'N/A';
  if (typeof experience === 'string' && experience.startsWith('[')) { try { experience = JSON.parse(experience); } catch (e) {} }
  if (Array.isArray(experience) && experience.length > 0) {
    const f = experience[0];
    experience = f.duration || f.title || f.position || 'N/A';
  } else if (typeof experience === 'object' && experience !== null) {
    experience = experience.title || experience.position || experience.duration || 'N/A';
  }

  if (typeof experience === 'number') {
    experience = `${experience} years`;
  } else if (typeof experience === 'string') {
    const normalizedExperience = experience.trim();
    const looksVerbose =
      normalizedExperience.length > 40 ||
      normalizedExperience.includes('\n') ||
      /[.!?]/.test(normalizedExperience) ||
      normalizedExperience.split(' ').length > 6;

    if (looksVerbose) {
      experience = experienceYearsValue
        ? `${experienceYearsValue} years`
        : 'Experience details';
    } else {
      experience = normalizedExperience || 'N/A';
    }
  }

  let skills = candidate.skills || candidate.key_skills || [];
  if (typeof skills === 'string') { try { skills = JSON.parse(skills); } catch (e) { skills = skills ? skills.split(',') : []; } }
  if (!Array.isArray(skills)) skills = [];
  const normalizedSkills = skills.map(s => typeof s === 'string' ? s : s.name || s.label || '').map(s => s.trim()).filter(Boolean);

  let highlights = candidate.highlights || [];
  if (typeof highlights === 'string') { try { highlights = JSON.parse(highlights); } catch (e) { highlights = highlights ? [highlights] : []; } }
  if (!Array.isArray(highlights)) highlights = [];

  const foundOnLabels = getFoundOnLabels(candidate);
  const funnelStage = candidate.funnel_stage || candidate.stage || 'Sourced';

  return { candidateId, name, initials, title, company, location, email, phone, matchScore, scoreDetails, experience, normalizedSkills, highlights, foundOnLabels, funnelStage, linkedinUrl };
};

// ─── Profile Slide-Over ──────────────────────────────────────────────────────

const ProfileSlideOver = ({ candidate, onClose, onStartOutreach, isDark, primaryColor }) => {
  const { candidateId, name, initials, title, company, location, email, phone, matchScore, scoreDetails, experience, normalizedSkills, highlights, foundOnLabels, funnelStage, linkedinUrl } = extractCandidate(candidate, 0);
  const scoreColor = matchScore >= 80 ? '#22C55E' : matchScore >= 60 ? '#F59E0B' : '#9CA3AF';

  const funnelStages = ['Sourced', 'Contacted', 'Interested', 'Shortlisted', 'Interviewed'];
  const currentStageIdx = funnelStages.indexOf(funnelStage);

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', justifyContent: 'flex-end',
  };

  const panel = {
    width: 420,
    maxWidth: '95vw',
    height: '100%',
    background: isDark ? '#111827' : '#fff',
    borderLeft: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideIn .25s ease',
    overflowY: 'auto',
  };

  return (
    <>
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .profile-close-btn:hover { background: ${isDark ? '#374151' : '#F3F4F6'} !important; }
        .profile-action-btn:hover { opacity: .85 !important; }
      `}</style>
      <div style={overlay} onClick={onClose}>
        <div style={panel} onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${isDark ? '#1F2937' : '#F3F4F6'}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Avatar */}
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${primaryColor}18`, border: `2px solid ${primaryColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 600, color: primaryColor, flexShrink: 0 }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#F9FAFB' : '#111827' }}>{name}</div>
                  <div style={{ fontSize: 13, color: isDark ? '#9CA3AF' : '#6B7280', marginTop: 2 }}>{title}</div>
                  <div style={{ fontSize: 12, color: isDark ? '#6B7280' : '#9CA3AF', marginTop: 1 }}>{company}</div>
                </div>
              </div>
              <button
                className="profile-close-btn"
                onClick={onClose}
                style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#9CA3AF' : '#6B7280'} strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Score + Location */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor, background: `${scoreColor}15`, padding: '4px 12px', borderRadius: 20, border: `1px solid ${scoreColor}30` }}>{matchScore}% Match</span>
                <ScoreDetailsInfo details={scoreDetails} candidateName={name} align="left" />
              </span>
              {location !== 'N/A' && <span style={{ fontSize: 12, color: isDark ? '#9CA3AF' : '#6B7280' }}>📍 {location}</span>}
              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                {foundOnLabels.map(s => <SourceBadge key={s} source={s} />)}
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto' }}>

            {/* Contact Info */}
            <Section title="Contact Info" isDark={isDark}>
              {email && <InfoRow label="Email" value={email} isDark={isDark} />}
              {phone && <InfoRow label="Phone" value={phone} isDark={isDark} />}
              {experience !== 'N/A' && <InfoRow label="Experience" value={experience} isDark={isDark} />}
              {linkedinUrl && (
                <InfoRow label="LinkedIn" value={
                  <a href={linkedinUrl} target="_blank" rel="noreferrer" style={{ color: '#0A66C2', textDecoration: 'none', fontSize: 12 }}>View Profile →</a>
                } isDark={isDark} />
              )}
            </Section>

            {/* Skills */}
            {normalizedSkills.length > 0 && (
              <Section title="Skills" isDark={isDark}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {normalizedSkills.map((s, i) => (
                    <span key={i} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>{s}</span>
                  ))}
                </div>
              </Section>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <Section title="Highlights" isDark={isDark}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {highlights.map((h, i) => {
                    const text = typeof h === 'string' ? h : h.text || String(h);
                    return (
                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                        <span style={{ color: '#22C55E', flexShrink: 0, marginTop: 1 }}>✓</span>
                        <span style={{ color: isDark ? '#D1D5DB' : '#374151', lineHeight: 1.5 }}>{text}</span>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Funnel Stage */}
            <Section title="Hiring Funnel" isDark={isDark}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 8, position: 'relative' }}>
                {funnelStages.map((stage, i) => {
                  const isActive = i <= currentStageIdx;
                  const isCurrent = i === currentStageIdx;
                  return (
                    <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: i < funnelStages.length - 1 ? 16 : 0, position: 'relative' }}>
                      {/* Line */}
                      {i < funnelStages.length - 1 && (
                        <div style={{ position: 'absolute', left: 7, top: 14, width: 2, height: 16, background: isActive ? primaryColor : (isDark ? '#374151' : '#E5E7EB'), borderRadius: 2 }} />
                      )}
                      <div style={{ width: 16, height: 16, borderRadius: '50%', background: isCurrent ? primaryColor : isActive ? `${primaryColor}50` : (isDark ? '#374151' : '#E5E7EB'), border: isCurrent ? `2px solid ${primaryColor}` : 'none', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isActive && !isCurrent && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
                      </div>
                      <span style={{ fontSize: 13, color: isCurrent ? primaryColor : isActive ? (isDark ? '#D1D5DB' : '#374151') : (isDark ? '#4B5563' : '#9CA3AF'), fontWeight: isCurrent ? 600 : 400 }}>{stage}</span>
                      {isCurrent && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${primaryColor}15`, color: primaryColor, marginLeft: 'auto' }}>Current</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>

          {/* Footer Actions */}
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${isDark ? '#1F2937' : '#F3F4F6'}`, display: 'flex', gap: 10 }}>
            <button
              className="profile-action-btn"
              onClick={() => onStartOutreach?.({ candidateIds: [candidateId], candidates: [candidate] })}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10, border: '1px solid #20C997', color: '#20C997', background: '#20C99710', cursor: 'pointer' }}
            >
              Send Outreach
            </button>
            <button
              className="profile-action-btn"
              onClick={onClose}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, color: isDark ? '#D1D5DB' : '#374151', background: 'transparent', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Section = ({ title, children, isDark }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const InfoRow = ({ label, value, isDark }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0', borderBottom: `1px solid ${isDark ? '#1F2937' : '#F9FAFB'}` }}>
    <span style={{ fontSize: 12, color: isDark ? '#6B7280' : '#9CA3AF', minWidth: 80, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 12, color: isDark ? '#E5E7EB' : '#111827', wordBreak: 'break-all' }}>{value}</span>
  </div>
);

// ─── Candidate Row ───────────────────────────────────────────────────────────

const CandidateRow = ({ candidate, index, isDark, primaryColor, isSelected, isExpanded, onSelect, onToggleExpand, onViewProfile, onStartOutreach }) => {
  const { candidateId, name, initials, title, company, location, email, phone, matchScore, scoreDetails, experience, normalizedSkills, highlights, foundOnLabels, funnelStage } = extractCandidate(candidate, index);

  const scoreColor = matchScore >= 80 ? '#22C55E' : matchScore >= 60 ? '#F59E0B' : '#9CA3AF';

  const statusColors = {
    Imported: { bg: '#F5F3FF', color: '#7C3AED', border: '#DDD6FE' },
    Sourced: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
    Contacted: { bg: '#F3E8FF', color: '#7C3AED', border: '#DDD6FE' },
    Interested: { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
    Shortlisted: { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
    Interviewed: { bg: '#F0FDF4', color: '#166534', border: '#86EFAC' },
  };
  // Imported candidates read "Imported" (not "Sourced") while at the initial stage; the underlying
  // funnelStage is kept for the progress tracker below.
  const isImported = String(candidate.source_type || candidate._source_type || '').toLowerCase() === 'imported';
  const displayStage = (isImported && (funnelStage === 'Sourced' || !funnelStage)) ? 'Imported' : funnelStage;
  const statusStyle = statusColors[displayStage] || statusColors.Sourced;

  return (
    <div style={{ borderBottom: `1px solid ${isDark ? '#1F2937' : '#F3F4F6'}` }}>
      {/* ── Main Row ── */}
      <div
        style={{ display: 'flex', alignItems: 'center', padding: '11px 16px', gap: 12, cursor: 'pointer', background: isExpanded ? (isDark ? '#1a2234' : '#FAFAFA') : 'transparent', transition: 'background .12s' }}
        onClick={() => onToggleExpand(candidateId)}
      >
        {/* Checkbox */}
        <div
          onClick={e => { e.stopPropagation(); onSelect(candidateId); }}
          style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${isSelected ? primaryColor : (isDark ? '#4B5563' : '#D1D5DB')}`, background: isSelected ? primaryColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', transition: 'all .12s' }}
        >
          {isSelected && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
        </div>

        {/* Avatar with source badge */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${primaryColor}15`, border: `1.5px solid ${primaryColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: primaryColor }}>
            {initials}
          </div>
          {foundOnLabels[0] && (
            <div style={{ position: 'absolute', bottom: -2, right: -2, background: isDark ? '#111827' : '#fff', borderRadius: 4, padding: 1, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}` }}>
              <SourceBadge source={foundOnLabels[0]} />
            </div>
          )}
        </div>

        {/* Name / Title / Company */}
        <div style={{ flex: '0 0 190px', minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#F9FAFB' : '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          <div style={{ fontSize: 11, color: isDark ? '#9CA3AF' : '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{title}</div>
          <div style={{ fontSize: 11, color: isDark ? '#6B7280' : '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{company}</div>
        </div>

        {/* Match Score */}
        <div style={{ flex: '0 0 78px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor, background: `${scoreColor}15`, padding: '3px 8px', borderRadius: 20, border: `1px solid ${scoreColor}25` }}>{matchScore}%</span>
            <ScoreDetailsInfo details={scoreDetails} candidateName={name} align="left" />
          </span>
        </div>

        {/* Status badge */}
        <div style={{ flex: '0 0 90px', textAlign: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, whiteSpace: 'nowrap' }}>{displayStage}</span>
        </div>

        {/* Location */}
        <div style={{ flex: '0 0 100px', fontSize: 11, color: isDark ? '#9CA3AF' : '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {location !== 'N/A' && `📍 ${location}`}
        </div>

        {/* Skills pills */}
        <div style={{ flex: 1, display: 'flex', gap: 4, overflow: 'hidden', alignItems: 'center' }}>
          {normalizedSkills.slice(0, 3).map((s, i) => (
            <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: isDark ? '#1e3a5f' : '#EFF6FF', color: isDark ? '#93C5FD' : '#1D4ED8', border: `1px solid ${isDark ? '#2563EB40' : '#BFDBFE'}`, whiteSpace: 'nowrap' }}>{s}</span>
          ))}
          {normalizedSkills.length > 3 && <span style={{ fontSize: 10, color: isDark ? '#6B7280' : '#9CA3AF' }}>+{normalizedSkills.length - 3}</span>}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onViewProfile(candidate)}
            style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: 'transparent', color: isDark ? '#D1D5DB' : '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            View
          </button>
          <button
            onClick={() => onStartOutreach?.({ candidateIds: [candidateId], candidates: [candidate] })}
            style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid #20C997', color: '#20C997', background: '#20C99710', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Outreach
          </button>
        </div>

        {/* Chevron */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#4B5563' : '#D1D5DB'} strokeWidth="2.5" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
            <path d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </div>

      {/* ── Expanded Accordion ── */}
      <div style={{ maxHeight: isExpanded ? 400 : 0, overflow: 'hidden', transition: 'max-height .3s ease' }}>
        {isExpanded && (
          <div style={{ padding: '0 16px 16px 76px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, background: isDark ? '#141e2e' : '#FAFAFA', borderTop: `1px solid ${isDark ? '#1F2937' : '#F3F4F6'}` }}>

            {/* Contact */}
            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Contact & Info</div>
              {[
                email && ['Email', email],
                phone && ['Phone', phone],
                location !== 'N/A' && ['Location', location],
                experience !== 'N/A' && ['Experience', experience],
                company !== 'N/A' && ['Company', company],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: isDark ? '#6B7280' : '#9CA3AF', minWidth: 68, flexShrink: 0 }}>{label}</span>
                  <span style={{ fontSize: 11, color: isDark ? '#E5E7EB' : '#111827', wordBreak: 'break-all' }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span style={{ fontSize: 10, color: isDark ? '#6B7280' : '#9CA3AF' }}>Sources:</span>
                <div style={{ display: 'flex', gap: 4 }}>{foundOnLabels.map(s => <SourceBadge key={s} source={s} />)}</div>
              </div>
            </div>

            {/* Skills + Highlights */}
            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Skills</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                {normalizedSkills.length > 0
                  ? normalizedSkills.map((s, i) => <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: isDark ? '#1e3a5f' : '#EFF6FF', color: isDark ? '#93C5FD' : '#1D4ED8', border: `1px solid ${isDark ? '#2563EB40' : '#BFDBFE'}` }}>{s}</span>)
                  : <span style={{ fontSize: 11, color: isDark ? '#6B7280' : '#9CA3AF' }}>No skills listed</span>
                }
              </div>
              {highlights.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Highlights</div>
                  {highlights.slice(0, 3).map((h, i) => {
                    const text = typeof h === 'string' ? h : h.text || String(h);
                    return (
                      <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 4 }}>
                        <span style={{ color: '#22C55E', flexShrink: 0 }}>✓</span>
                        <span style={{ color: isDark ? '#D1D5DB' : '#374151', lineHeight: 1.5 }}>{text}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Funnel */}
            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Funnel Stage</div>
              {['Sourced', 'Contacted', 'Interested', 'Shortlisted', 'Interviewed'].map((stage, i) => {
                const current = stage === funnelStage;
                return (
                  <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: current ? '#22C55E' : i < ['Sourced', 'Contacted', 'Interested', 'Shortlisted', 'Interviewed'].indexOf(funnelStage) ? `${primaryColor}60` : (isDark ? '#374151' : '#E5E7EB'), flexShrink: 0, border: current ? '2px solid #22C55E' : 'none' }} />
                    <span style={{ fontSize: 12, color: current ? '#22C55E' : (isDark ? '#6B7280' : '#9CA3AF'), fontWeight: current ? 600 : 400 }}>{stage}</span>
                  </div>
                );
              })}
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: isDark ? '#1a2a3a' : '#FFF7ED', border: `1px solid ${isDark ? '#374151' : '#FED7AA'}`, fontSize: 11 }}>
                <span style={{ color: '#F59E0B', fontWeight: 600 }}>Next: </span>
                <span style={{ color: isDark ? '#D1D5DB' : '#374151' }}>Send follow-up email</span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  onClick={() => onViewProfile(candidate)}
                  style={{ flex: 1, fontSize: 11, padding: '6px 0', borderRadius: 6, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: 'transparent', color: isDark ? '#D1D5DB' : '#374151', cursor: 'pointer' }}
                >
                  View Profile
                </button>
                <button
                  onClick={() => onStartOutreach?.({ candidateIds: [candidateId], candidates: [candidate] })}
                  style={{ flex: 1, fontSize: 11, padding: '6px 0', borderRadius: 6, border: '1px solid #20C997', color: '#20C997', background: '#20C99710', cursor: 'pointer' }}
                >
                  Outreach
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const CandidateResults = ({
  candidates = [],
  sourcingStats = {},
  onStartOutreach,
  sessionId,
  searchId,
  jdId,
  jobTitle,
  postedChannels = [],
  applicantSyncEnabled = true,
  onSyncApplicants,
  onSyncReplies,
  syncingApplicants = false,
  lastApplicantsSyncTime = null,
  onViewCandidate,
  onCandidateRowClick,
  googleLogs = [],
  linkedinLogs = [],
  resumeBoxLogs = [],
  googleProcessing = false,
  linkedinProcessing = false,
  resumeBoxProcessing = false,
  googleCandidateCount = 0,
  linkedinCandidateCount = 0,
  resumeBoxCandidateCount = 0,
  showSourcingLogs = false,
  channelFilter = [],
  selectedSourcingChannels = [],
  googleSearchFoundCount = 0,
  linkedinSearchFoundCount = 0,
  enrichedCandidateCount = 0,
  matchedCandidateCount = 0,
  defaultStatusFilter = [],
  refreshToken = 0,
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { getSyncState, refreshSyncState } = useSyncContext();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const showApplicantSync = applicantSyncEnabled !== false;
  const canStartOutreach = typeof onStartOutreach === 'function';
  const effectiveJdId = jdId || sessionId;
  const emailSyncState = getSyncState(searchId);
  const [processTrackingSyncing, setProcessTrackingSyncing] = useState(false);
  const [subjectSyncing, setSubjectSyncing] = useState(false);
  const [processTrackingStats, setProcessTrackingStats] = useState({
    totalMails: 0,
    parsedResumes: 0,
    duplicateResumes: 0,
    lastSync: null,
  });
  const [subjectSyncLogs, setSubjectSyncLogs] = useState([]);
  const subjectSyncEventSourceRef = useRef(null);
  const subjectSyncPollRef = useRef(null);
  const subjectSyncRetryRef = useRef(null);
  const emailSyncing = Boolean(emailSyncState.syncing || subjectSyncing);
  const emailSyncPhase = emailSyncState.phase || '';
  const emailSyncLogs = Array.isArray(emailSyncState.logs) ? emailSyncState.logs : [];
  const emailSyncCompletedAt = emailSyncState.completedAt || null;
  const emailSyncResult = emailSyncState.syncResult || {};

  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [expandedCandidateIds, setExpandedCandidateIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => (
    Array.isArray(defaultStatusFilter) ? defaultStatusFilter.filter(Boolean) : []
  ));
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const [serverCandidates, setServerCandidates] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverLoading, setServerLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const pageCacheRef = useRef({});
  const previousSessionIdRef = useRef(sessionId);
  const getCandidateSelectionKey = useCallback((candidate, index = 0) => candidate?.id || candidate?.candidate_id || `candidate_${index}`, []);

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('warning');
  const [showToast, setShowToast] = useState(false);
  const [activeSourcingLog, setActiveSourcingLog] = useState(null);
  // File-based full logs fetched from the API when a log modal is opened
  const [fileLogsState, setFileLogsState] = useState({
    google: { loading: false, logs: [], error: null, fetched: false },
    linkedin: { loading: false, logs: [], error: null, fetched: false },
    resumebox: { loading: false, logs: [], error: null, fetched: false },
  });
  const [workflowSnapshot, setWorkflowSnapshot] = useState(null);
  const [dbProcessStatus, setDbProcessStatus] = useState(null);
  const [sourcingActivityCollapsed, setSourcingActivityCollapsed] = useState(true);
  const [candidateSyncCollapsed, setCandidateSyncCollapsed] = useState(true);
  // Subject-based email sync
  const [subjectKey, setSubjectKey] = useState('');
  const [subjectSyncLoading, setSubjectSyncLoading] = useState(false);
  const [, setSubjectSyncResult] = useState(null); // null | 'started' | 'error' | 'no_gmail' | 'connecting_gmail'
  const [, setSubjectSyncMessage] = useState('');
  const [subjectMailAuthPrompt, setSubjectMailAuthPrompt] = useState(null);
  const [subjectKeySaving, setSubjectKeySaving] = useState(false);
  const [, setSubjectKeySaved] = useState(false); // tracks save state for inline editor transitions
  const [subjectKeyEditing, setSubjectKeyEditing] = useState(false);
  const [subjectMailSyncEnabled, setSubjectMailSyncEnabled] = useState(false);
  const [unparsedResumeCount, setUnparsedResumeCount] = useState(0);
  const [unparsedResumeLoading, setUnparsedResumeLoading] = useState(false);
  const [reparsingUnparsedResumes, setReparsingUnparsedResumes] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [hasPersistedJobPosted, setHasPersistedJobPosted] = useState(false);
  const [persistedPostedPlatform, setPersistedPostedPlatform] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [scoreConfigOpen, setScoreConfigOpen] = useState(false);
  const [serverEmailCount, setServerEmailCount] = useState(null);
  const [serverLinkedinCount, setServerLinkedinCount] = useState(null);
  const [serverStatusCounts, setServerStatusCounts] = useState({});
  const defaultStatusFilterKey = Array.isArray(defaultStatusFilter)
    ? defaultStatusFilter.filter(Boolean).join(',')
    : '';
  const postedChannelsKey = Array.isArray(postedChannels)
    ? postedChannels.map((channel) => String(channel || '').trim().toLowerCase()).filter(Boolean).sort().join('|')
    : '';
  const normalizedPostedChannels = useMemo(
    () => (
      postedChannelsKey
        ? postedChannelsKey.split('|').filter(Boolean)
        : []
    ),
    [postedChannelsKey],
  );

  // Profile slide-over state
  const [profileCandidate, setProfileCandidate] = useState(null);

  // ─── API Calls ─────────────────────────────────────────────────────────────

  const fetchLastSyncTime = useCallback(async () => {
    if (!sessionId) return;
    try {
      const data = await aiAutomationApi.get(`/job-postings/by-session/${sessionId}`);
      const jp = data?.job_posting;
      if (jp) {
        const hasN = !!jp.naukri_job_id;
        const hasL = !!jp.linkedin_job_id || !!jp.linkedin_job_url;
        const hasLiveNaukri = hasN || normalizedPostedChannels.includes('naukri');
        const hasLiveLinkedin = hasL || normalizedPostedChannels.includes('linkedin');
        setHasPersistedJobPosted(hasLiveNaukri || hasLiveLinkedin);
        setPersistedPostedPlatform(hasLiveNaukri && hasLiveLinkedin ? 'both' : hasLiveNaukri ? 'naukri' : hasLiveLinkedin ? 'linkedin' : '');
        if (jp.last_sync) setLastSyncTime(new Date(jp.last_sync));
        setProcessTrackingSyncing(Boolean(jp.sync_process));
        setProcessTrackingStats({
          totalMails: Number(jp.total_mails || 0),
          parsedResumes: Number(jp.parsed_resumes || 0),
          duplicateResumes: Number(jp.duplicate_resumes || 0),
          lastSync: jp.last_sync || null,
        });
        setSyncing(jp.sync_process === true);
      } else {
        const hasFallbackNaukri = normalizedPostedChannels.includes('naukri');
        const hasFallbackLinkedin = normalizedPostedChannels.includes('linkedin');
        setHasPersistedJobPosted(hasFallbackNaukri || hasFallbackLinkedin);
        setPersistedPostedPlatform(hasFallbackNaukri && hasFallbackLinkedin ? 'both' : hasFallbackNaukri ? 'naukri' : hasFallbackLinkedin ? 'linkedin' : '');
        setProcessTrackingSyncing(false);
        setProcessTrackingStats({ totalMails: 0, parsedResumes: 0, duplicateResumes: 0, lastSync: null });
      }
    } catch {
      const hasFallbackNaukri = normalizedPostedChannels.includes('naukri');
      const hasFallbackLinkedin = normalizedPostedChannels.includes('linkedin');
      setHasPersistedJobPosted(hasFallbackNaukri || hasFallbackLinkedin);
      setPersistedPostedPlatform(hasFallbackNaukri && hasFallbackLinkedin ? 'both' : hasFallbackNaukri ? 'naukri' : hasFallbackLinkedin ? 'linkedin' : '');
      setProcessTrackingSyncing(false);
      setProcessTrackingStats({ totalMails: 0, parsedResumes: 0, duplicateResumes: 0, lastSync: null });
    }
  }, [sessionId, normalizedPostedChannels]);

  const fetchUnparsedResumeCount = useCallback(async () => {
    if (!sessionId) {
      setUnparsedResumeCount(0);
      return 0;
    }
    setUnparsedResumeLoading(true);
    try {
      const data = await aiAutomationApi.get(`/email/session-unparsed-resumes/${encodeURIComponent(sessionId)}`);
      const count = Number(data?.count || 0);
      setUnparsedResumeCount(count);
      return count;
    } catch {
      setUnparsedResumeCount(0);
      return 0;
    } finally {
      setUnparsedResumeLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setSyncing(false); setLastSyncTime(null); setHasPersistedJobPosted(false); setPersistedPostedPlatform(''); setProcessTrackingSyncing(false); setProcessTrackingStats({ totalMails: 0, parsedResumes: 0, duplicateResumes: 0, lastSync: null }); setUnparsedResumeCount(0); setReparsingUnparsedResumes(false);
    // Reset fetched flag so opening a log modal always re-fetches for the new session
    setFileLogsState({ google: { loading: false, logs: [], error: null, fetched: false }, linkedin: { loading: false, logs: [], error: null, fetched: false }, resumebox: { loading: false, logs: [], error: null, fetched: false } });
    if (sessionId) {
      fetchLastSyncTime();
      fetchUnparsedResumeCount();
    }
  }, [sessionId, fetchLastSyncTime, fetchUnparsedResumeCount]);

  // Load saved subject key for this session
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    aiAutomationApi.get(`/email/session-subject-key/${sessionId}`)
      .then(data => { if (!cancelled && data?.subject_key) { setSubjectKey(data.subject_key); setSubjectKeySaved(true); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    const cid = getCid();
    if (!cid) {
      setSubjectMailSyncEnabled(false);
      return undefined;
    }
    aiAutomationApi.get(`/email/settings/${cid}`)
      .then((data) => {
        if (!cancelled) setSubjectMailSyncEnabled(Boolean(data?.settings?.sync_mail_subject));
      })
      .catch(() => {
        if (!cancelled) setSubjectMailSyncEnabled(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setWorkflowSnapshot(null);
      setDbProcessStatus(null);
      return undefined;
    }

    let cancelled = false;
    const cid = getCid();
    if (cid) {
      aiAutomationApi
        .getWorkflowSnapshot(sessionId, cid)
        .then((data) => {
          if (!cancelled) setWorkflowSnapshot(data || null);
        })
        .catch(() => {
          if (!cancelled) setWorkflowSnapshot(null);
        });
    } else {
      setWorkflowSnapshot(null);
    }

    aiAutomationApi
      .get(`/process-status/${sessionId}`)
      .then((data) => {
        if (!cancelled) setDbProcessStatus(data || null);
      })
      .catch(() => {
        if (!cancelled) setDbProcessStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    sessionId,
    googleProcessing,
    linkedinProcessing,
    resumeBoxProcessing,
    googleLogs.length,
    linkedinLogs.length,
    resumeBoxLogs.length,
    googleCandidateCount,
    linkedinCandidateCount,
    resumeBoxCandidateCount,
  ]);

  // Fetch full sourcing logs from the backend when a log modal is opened
  useEffect(() => {
    if (!activeSourcingLog || !sessionId) return;
    const platformKey = activeSourcingLog;
    setFileLogsState((prev) => ({
      ...prev,
      [platformKey]: { loading: true, logs: prev[platformKey]?.logs || [], error: null, fetched: false },
    }));
    aiAutomationApi
      .get(`/logs/session/${sessionId}/sourcing/${platformKey}`)
      .then((data) => {
        setFileLogsState((prev) => ({
          ...prev,
          [platformKey]: { loading: false, logs: data?.logs || [], error: null, fetched: true },
        }));
      })
      .catch(() => {
        setFileLogsState((prev) => ({
          ...prev,
          [platformKey]: { loading: false, logs: prev[platformKey]?.logs || [], error: 'Could not load full log file.', fetched: true },
        }));
      });
  }, [activeSourcingLog, sessionId]);

  useEffect(() => {
    if ((!syncing && !processTrackingSyncing) || !sessionId) return;
    let count = 0;
    const interval = setInterval(async () => {
      if (++count >= 60) { clearInterval(interval); setSyncing(false); setProcessTrackingSyncing(false); return; }
      try {
        const data = await aiAutomationApi.get(`/job-postings/by-session/${sessionId}`);
        if (data?.job_posting?.sync_process === false) {
          clearInterval(interval); setSyncing(false); setProcessTrackingSyncing(false);
          if (data.job_posting.last_sync) setLastSyncTime(new Date(data.job_posting.last_sync));
          const completedStats = {
            totalMails: Number(data.job_posting.total_mails || 0),
            parsedResumes: Number(data.job_posting.parsed_resumes || 0),
            duplicateResumes: Number(data.job_posting.duplicate_resumes || 0),
            lastSync: data.job_posting.last_sync || null,
          };
          setProcessTrackingStats(completedStats);
          const unparsedCount = await fetchUnparsedResumeCount();
          setToastMessage(buildSyncCompleteToast(completedStats, unparsedCount)); setToastType('success'); setShowToast(true);
        } else if (data?.job_posting?.sync_process === true) {
          setProcessTrackingSyncing(true);
          setProcessTrackingStats({
            totalMails: Number(data.job_posting.total_mails || 0),
            parsedResumes: Number(data.job_posting.parsed_resumes || 0),
            duplicateResumes: Number(data.job_posting.duplicate_resumes || 0),
            lastSync: data.job_posting.last_sync || null,
          });
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [syncing, processTrackingSyncing, sessionId, fetchUnparsedResumeCount]);

  useEffect(() => {
    if (!searchId || (!emailSyncState.syncing && !subjectSyncing)) return undefined;
    let cancelled = false;
    let checks = 0;
    const interval = setInterval(async () => {
      if (cancelled) return;
      checks += 1;
      try {
        const refreshed = await refreshSyncState(searchId);
        const stillSyncing = Boolean(refreshed?.email_sync?.syncing);
        if (!stillSyncing || checks >= 90) {
          clearInterval(interval);
          setSubjectSyncing(false);
          fetchLastSyncTime();
          fetchUnparsedResumeCount();
        }
      } catch (error) {
        console.warn('[SubjectSync] Sync state reconciliation failed', error);
      }
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    emailSyncState.syncing,
    fetchLastSyncTime,
    fetchUnparsedResumeCount,
    refreshSyncState,
    searchId,
    subjectSyncing,
  ]);

  const loadCandidates = useCallback(async () => {
    if (!sessionId) return;
    const cacheKey = `${refreshToken}_${debouncedSearchTerm}_${sourceFilter.join(',') || 'none'}_${statusFilter.join(',') || 'all-statuses'}`;
    const cached = pageCacheRef.current[cacheKey]?.[currentPage];
    if (cached) {
      setServerCandidates(cached.data); setTotalRecords(cached.totalRecords); setTotalPages(cached.totalPages);
      if (cached.emailCount !== undefined) setServerEmailCount(cached.emailCount);
      if (cached.linkedinCount !== undefined) setServerLinkedinCount(cached.linkedinCount);
      if (cached.statusCounts !== undefined) setServerStatusCounts(cached.statusCounts || {});
      return;
    }
    try {
      setServerLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(ITEMS_PER_PAGE));
      params.set('session_id', sessionId);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (sourceFilter.length > 0) params.set('source', sourceFilter.join(','));
      if (statusFilter.length > 0) params.set('status', statusFilter.join(','));
      params.set('exclude_rejected', 'true'); // sourcing hides rejected/declined (Overview shows them)
      const res = await aiAutomationApi.get(`/automation_candidates/all?${params}`);
      let list = [], total = 0, pages = 1, emailC = null, liC = null, statusC = {};
      if (res && !Array.isArray(res) && res.data) {
        list = res.data; total = res.total_records || 0; pages = res.total_pages || 1; emailC = res.email_count; liC = res.linkedin_count; statusC = res.status_counts || {};
      } else if (Array.isArray(res)) { list = res; total = res.length; pages = Math.max(1, Math.ceil(res.length / ITEMS_PER_PAGE)); }
      if (!pageCacheRef.current[cacheKey]) pageCacheRef.current[cacheKey] = {};
      pageCacheRef.current[cacheKey][currentPage] = { data: list, totalRecords: total, totalPages: pages, emailCount: emailC, linkedinCount: liC, statusCounts: statusC };
      setServerCandidates(list); setTotalRecords(total); setTotalPages(pages);
      if (emailC != null) setServerEmailCount(emailC);
      if (liC != null) setServerLinkedinCount(liC);
      setServerStatusCounts(statusC || {});
    } catch (e) { console.error(e); } finally { setServerLoading(false); }
  }, [sessionId, currentPage, debouncedSearchTerm, sourceFilter, statusFilter, refreshToken]);

  // External refresh (e.g. after a candidate is rejected): drop the cache so the list re-fetches
  // fresh and the now-terminal candidate disappears immediately.
  useEffect(() => { pageCacheRef.current = {}; }, [refreshToken]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm); setCurrentPage(1); pageCacheRef.current = {};
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  useEffect(() => { setCurrentPage(1); pageCacheRef.current = {}; }, [sourceFilter, statusFilter]);

  useEffect(() => {
    setStatusFilter(defaultStatusFilterKey ? defaultStatusFilterKey.split(',') : []);
  }, [defaultStatusFilterKey]);

  useEffect(() => () => {
    if (subjectSyncEventSourceRef.current) {
      subjectSyncEventSourceRef.current.close();
      subjectSyncEventSourceRef.current = null;
    }
    if (subjectSyncPollRef.current) {
      clearInterval(subjectSyncPollRef.current);
      subjectSyncPollRef.current = null;
    }
  }, []);

  useEffect(() => {
    const sessionChanged = previousSessionIdRef.current !== sessionId;
    previousSessionIdRef.current = sessionId;
    setSelectedCandidates([]);
    setServerStatusCounts({});
    setExpandedCandidateIds([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSourceFilter([]);
    if (sessionChanged) setStatusFilter([]);
    setFilterDropdownOpen(false);
    setCurrentPage(1);
    pageCacheRef.current = {};
  }, [sessionId]);

  const handleRefreshCandidates = async () => {
    if (!sessionId || refreshing) return;
    setRefreshing(true);
    try {
      pageCacheRef.current = {}; setCurrentPage(1);
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', String(ITEMS_PER_PAGE));
      params.set('session_id', sessionId);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (sourceFilter.length > 0) params.set('source', sourceFilter.join(','));
      if (statusFilter.length > 0) params.set('status', statusFilter.join(','));
      params.set('exclude_rejected', 'true'); // sourcing hides rejected/declined (Overview shows them)
      const res = await aiAutomationApi.get(`/automation_candidates/all?${params}`);
      let list = [], total = 0, pages = 1, emailC = null, liC = null, statusC = {};
      if (res && !Array.isArray(res) && res.data) {
        list = res.data; total = res.total_records || 0; pages = res.total_pages || 1; emailC = res.email_count; liC = res.linkedin_count; statusC = res.status_counts || {};
      } else if (Array.isArray(res)) { list = res; total = res.length; pages = Math.max(1, Math.ceil(res.length / ITEMS_PER_PAGE)); }
      setServerCandidates(list); setTotalRecords(total); setTotalPages(pages);
      if (emailC != null) setServerEmailCount(emailC);
      if (liC != null) setServerLinkedinCount(liC);
      setToastMessage(`Refreshed ${total} candidates`); setToastType('success'); setShowToast(true);
    } catch { setToastMessage('Failed to refresh'); setToastType('error'); setShowToast(true); }
    finally { setRefreshing(false); }
  };

  const watchSubjectSyncLogs = useCallback((syncId) => {
    if (!syncId) return;
    if (subjectSyncEventSourceRef.current) {
      subjectSyncEventSourceRef.current.close();
      subjectSyncEventSourceRef.current = null;
    }
    if (subjectSyncPollRef.current) {
      clearInterval(subjectSyncPollRef.current);
      subjectSyncPollRef.current = null;
    }
    setSubjectSyncLogs([{ message: 'Subject sync started...', status: 'info', timestamp: new Date().toISOString() }]);
    const es = new EventSource(`${AI_SSE_URL}/email/sync-stream/${syncId}`);
    subjectSyncEventSourceRef.current = es;
    let finalized = false;
    const appendLogs = (logs = []) => {
      if (!Array.isArray(logs) || logs.length === 0) return;
      setSubjectSyncLogs((prev) => {
        const next = [...prev];
        logs.forEach((entry) => {
          const message = typeof entry === 'string' ? entry : (entry?.message || entry?.logMessage || '');
          if (!message) return;
          next.push({
            ...(typeof entry === 'string' ? {} : entry),
            message,
            timestamp: entry?.timestamp || new Date().toISOString(),
            status: entry?.status || entry?.phase || 'info',
          });
        });
        return next.slice(-200);
      });
    };
    const finalizeSubjectSync = async (data = {}, failed = false) => {
      if (finalized) return;
      finalized = true;
      appendLogs(data.new_logs || []);
      if (failed && isMailProviderAuthError(data)) {
        appendLogs(['Mail authorization required. Opening authorization...']);
        setSubjectSyncResult('connecting_gmail');
        setSubjectSyncMessage('Mail provider needs to be reconnected. Opening authorization...');
        const meta = getMetadata();
        const cid = meta?.cid || meta?.companyId;
        const connected = await handleMailProviderReconnect({
          cid,
          error: data,
          onConnecting: (providerLabel) => {
            setSubjectSyncResult('connecting_gmail');
            setSubjectSyncMessage(`Connecting ${providerLabel}... please complete the login in the popup.`);
          },
          onConnected: (email, providerLabel) => {
            setSubjectSyncMessage(`${providerLabel} connected${email ? ` as ${email}` : ''} - restarting subject sync...`);
          },
          onError: (msg) => {
            setSubjectSyncResult('error');
            setSubjectSyncMessage(msg || 'Failed to connect mail provider.');
          },
          onPopupBlocked: (popupErr, providerLabel, provider) => {
            setSubjectSyncResult('connecting_gmail');
            setSubjectSyncMessage(`${providerLabel} authorization is required. Click Connect to continue subject sync.`);
            setSubjectMailAuthPrompt({
              authUrl: popupErr?.authUrl,
              provider,
              providerLabel,
              subjectKey,
              cid,
              connecting: false,
              error: '',
            });
          },
        });
        setSubjectSyncing(false);
        if (subjectSyncPollRef.current) {
          clearInterval(subjectSyncPollRef.current);
          subjectSyncPollRef.current = null;
        }
        subjectSyncEventSourceRef.current?.close();
        subjectSyncEventSourceRef.current = null;
        if (connected) {
          window.setTimeout(() => {
            subjectSyncRetryRef.current?.();
          }, 800);
        }
        return;
      }
      if (!failed) appendLogs(['Subject sync complete']);
      if (data.result) {
        setProcessTrackingStats((prev) => ({
          ...prev,
          totalMails: Number(data.result.total_mails || data.result.total_scanned || prev.totalMails || 0),
          parsedResumes: Number(data.result.parsed_resumes || data.result.resumes_parsed || prev.parsedResumes || 0),
        }));
      }
      setSubjectSyncing(false);
      if (subjectSyncPollRef.current) {
        clearInterval(subjectSyncPollRef.current);
        subjectSyncPollRef.current = null;
      }
      subjectSyncEventSourceRef.current?.close();
      subjectSyncEventSourceRef.current = null;
      if (searchId) {
        await refreshSyncState(searchId).catch((error) => {
          console.warn('[SubjectSync] Failed to refresh email sync state', error);
        });
      }
      fetchLastSyncTime();
      fetchUnparsedResumeCount();
      [5000, 15000, 30000].forEach((delay) => {
        setTimeout(() => {
          if (sessionId) {
            if (searchId) {
              refreshSyncState(searchId).catch((error) => {
                console.warn('[SubjectSync] Delayed email sync refresh failed', error);
              });
            }
            fetchLastSyncTime();
            fetchUnparsedResumeCount();
          }
        }, delay);
      });
    };
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        appendLogs(data.new_logs || []);
      } catch (_) {}
    };
    es.addEventListener('complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        finalizeSubjectSync(data);
      } catch (_) {
        finalizeSubjectSync();
      }
    });
    es.addEventListener('error', (event) => {
      if (event?.data) {
        try {
          const data = JSON.parse(event.data);
          appendLogs([`Subject sync failed: ${data.error || 'Unknown error'}`]);
          finalizeSubjectSync(data, true);
        } catch (_) {}
      }
    });
    subjectSyncPollRef.current = setInterval(async () => {
      try {
        const statusData = await aiAutomationApi.get(`/email/sync-status/${syncId}`);
        if (statusData?.status === 'completed') {
          finalizeSubjectSync(statusData);
        } else if (statusData?.status === 'failed' || statusData?.status === 'unknown') {
          appendLogs([statusData?.error ? `Subject sync failed: ${statusData.error}` : 'Subject sync status unavailable']);
          finalizeSubjectSync(statusData, true);
        }
      } catch (error) {
        console.warn('[SubjectSync] Poll status failed', error);
      }
    }, 5000);
  }, [fetchLastSyncTime, fetchUnparsedResumeCount, refreshSyncState, searchId, sessionId, subjectKey]);

  const _doSubjectSyncRequest = async (key) => {
    const data = await aiAutomationApi.post('/email/sync-by-subject', { subject_key: key, search_id: searchId, session_id: sessionId, max_results: 100 });
    if (data?.error_code === 'SUBJECT_SYNC_DISABLED' || data?.status === 'disabled') {
      const disabledErr = new Error(data?.message || 'Subject-based mail sync is disabled.');
      disabledErr._subjectSyncDisabled = true;
      throw disabledErr;
    }
    if (isMailProviderAuthError(data)) {
      const mailErr = new Error(data?.message || 'Mail provider authorization required');
      mailErr.response = { data };
      mailErr._mailAuthNeeded = true;
      throw mailErr;
    }
    return data;
  };

  const handleSubjectSyncStartResponse = async (data) => {
    if (data?.reason === 'sync_already_in_progress') {
      setSubjectSyncResult('started');
      setSubjectSyncMessage(data?.message || 'Sync is already processing for this session.');
      if (data.sync_id) {
        setSubjectSyncing(true);
        watchSubjectSyncLogs(data.sync_id);
      } else if (searchId) {
        const refreshed = await refreshSyncState(searchId).catch((error) => {
          console.warn('[SubjectSync] Failed to refresh already-running sync state', error);
          return null;
        });
        const stillSyncing = Boolean(refreshed?.email_sync?.syncing);
        setSubjectSyncing(stillSyncing);
        if (!stillSyncing) {
          setSubjectSyncResult('started');
          setSubjectSyncMessage('Previous sync already completed.');
          fetchLastSyncTime();
          fetchUnparsedResumeCount();
        }
      } else {
        setSubjectSyncing(false);
      }
      return;
    }

    if (data?.status === 'accepted') {
      setSubjectSyncing(true);
      setSubjectSyncResult('started');
      setSubjectSyncMessage(`Subject sync started — ${data.sync_id ? `ID ${data.sync_id.slice(0, 8)}…` : 'running in background'}`);
      if (data.sync_id) watchSubjectSyncLogs(data.sync_id);
      return;
    }

    setSubjectSyncResult('error');
    setSubjectSyncMessage(data?.message || 'Unexpected response from server.');
  };

  const handleSubjectMailAuthConnect = async () => {
    const prompt = subjectMailAuthPrompt;
    if (!prompt?.authUrl) return;
    const provider = String(prompt.provider || 'gmail').toLowerCase() === 'outlook' ? 'outlook' : 'gmail';
    const providerLabel = provider === 'outlook' ? 'Outlook' : 'Gmail';
    setSubjectMailAuthPrompt({ ...prompt, connecting: true, error: '' });
    setSubjectSyncResult('connecting_gmail');
    setSubjectSyncMessage(`Connecting ${providerLabel}… please complete authorization in the popup.`);

    try {
      const result = await openMailOAuthUrlPopup({ authUrl: prompt.authUrl, provider });
      let email = result.email || '';

      if (provider === 'gmail' && result.pendingSwitch) {
        const confirmed = window.confirm(
          `Switch Gmail from ${result.previousEmail} to ${result.newEmail}?\n\n` +
          'Past email conversations from the previous account will no longer sync.\n\n' +
          'Click OK to confirm the switch, or Cancel to keep the current account.'
        );
        if (confirmed) {
          const confirmResult = await confirmGmailAccountSwitch(prompt.cid);
          email = confirmResult.gmail_email || result.newEmail || '';
        } else {
          await cancelGmailAccountSwitch(prompt.cid);
          setSubjectMailAuthPrompt({ ...prompt, connecting: false, error: 'Gmail account switch was not confirmed.' });
          setSubjectSyncMessage('Gmail account switch was not confirmed.');
          return;
        }
      }

      setSubjectMailAuthPrompt(null);
      setSubjectSyncLoading(true);
      setSubjectSyncMessage(`${providerLabel} connected${email ? ` as ${email}` : ''} — starting sync…`);
      await new Promise(resolve => setTimeout(resolve, 800));
      const data = await _doSubjectSyncRequest(prompt.subjectKey);
      await handleSubjectSyncStartResponse(data);
    } catch (err) {
      setSubjectSyncResult('error');
      setSubjectSyncMessage(err?.message || `Failed to connect ${providerLabel}.`);
      setSubjectMailAuthPrompt({
        ...prompt,
        connecting: false,
        error: err?.message || `Failed to connect ${providerLabel}.`,
      });
    } finally {
      setSubjectSyncLoading(false);
    }
  };

  // Called by "Sync Replies" button to also run subject sync if a key is saved
  const handleSubjectSync = async () => {
    const key = (subjectKey || '').trim();
    if (!key || !searchId) return;
    if (!subjectMailSyncEnabled) {
      setSubjectSyncResult('error');
      setSubjectSyncMessage('Subject-based mail sync is disabled. Enable it in Account > Mail Settings.');
      return;
    }
    setSubjectSyncLoading(true);
    setSubjectSyncResult(null);
    setSubjectSyncMessage('');
    try {
      let data;
      try {
        data = await _doSubjectSyncRequest(key);
      } catch (err) {
        const isMailAuthErr = err._mailAuthNeeded || isMailProviderAuthError(err) ||
          (err?.message || '').toLowerCase().includes('gmail authorization has expired') ||
          (err?.message || '').toLowerCase().includes('reconnect your gmail') ||
          (err?.message || '').toLowerCase().includes('gmail is not connected') ||
          (err?.message || '').toLowerCase().includes('outlook authorization has expired') ||
          (err?.message || '').toLowerCase().includes('reconnect outlook') ||
          (err?.message || '').toLowerCase().includes('outlook is not connected');
        if (isMailAuthErr) {
          setSubjectSyncResult('connecting_gmail');
          setSubjectSyncMessage('Mail provider needs to be reconnected. Opening authorization…');
          const meta = getMetadata();
          const cid = meta?.cid || meta?.companyId;
          const connected = await handleMailProviderReconnect({
            cid,
            error: err,
            onConnecting: (providerLabel) => {
              setSubjectSyncResult('connecting_gmail');
              setSubjectSyncMessage(`Connecting ${providerLabel}… please complete the login in the popup.`);
            },
            onConnected: (email, providerLabel) => {
              setSubjectSyncMessage(`${providerLabel} connected${email ? ` as ${email}` : ''} — starting sync…`);
            },
            onError: (msg) => {
              setSubjectSyncResult('error');
              setSubjectSyncMessage(msg || 'Failed to connect mail provider.');
            },
            onPopupBlocked: (popupErr, providerLabel, provider) => {
              setSubjectSyncResult('connecting_gmail');
              setSubjectSyncMessage(`${providerLabel} authorization is required. Click Connect to continue subject sync.`);
              setSubjectMailAuthPrompt({
                authUrl: popupErr?.authUrl,
                provider,
                providerLabel,
                subjectKey: key,
                cid,
                connecting: false,
                error: '',
              });
            },
          });
          if (!connected) { setSubjectSyncLoading(false); return; }
          await new Promise(resolve => setTimeout(resolve, 800));
          data = await _doSubjectSyncRequest(key);
        } else {
          throw err;
        }
      }
      await handleSubjectSyncStartResponse(data);
    } catch (err) {
      setSubjectSyncing(false);
      setSubjectSyncResult('error');
      setSubjectSyncMessage(err?.message || 'Failed to start subject sync.');
    } finally {
      setSubjectSyncLoading(false);
    }
  };

  useEffect(() => {
    subjectSyncRetryRef.current = handleSubjectSync;
  });

  // Save the subject key to the DB for this session
  const handleSaveSubjectKey = async () => {
    if (!sessionId) return;
    setSubjectKeySaving(true);
    setSubjectKeySaved(false);
    try {
      await aiAutomationApi.put('/email/session-subject-key', { session_id: sessionId, subject_key: (subjectKey || '').trim() });
      setSubjectKeySaved(true);
      setSubjectKeyEditing(false);
    } catch (err) {
      setToastMessage(err?.message || 'Failed to save subject key.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setSubjectKeySaving(false);
    }
  };

  const handleReparseUnparsedResumes = async () => {
    if (!sessionId || reparsingUnparsedResumes || unparsedResumeCount <= 0) return;
    setReparsingUnparsedResumes(true);
    try {
      const data = await aiAutomationApi.post(`/email/session-unparsed-resumes/${encodeURIComponent(sessionId)}/reparse`, {});
      const count = Number(data?.count || 0);
      setToastMessage(data?.message || `Submitted ${count} resume(s) for re-parsing.`);
      setToastType('success');
      setShowToast(true);
      [5000, 15000, 30000, 60000].forEach((delay) => {
        setTimeout(() => {
          if (sessionId) fetchUnparsedResumeCount();
        }, delay);
      });
    } catch (err) {
      setToastMessage(err?.message || 'Failed to start re-parsing.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setReparsingUnparsedResumes(false);
    }
  };

  const handleSyncPostedJob = async () => {
    if (applicantSyncing) return;
    setSyncing(true);
    if (typeof onSyncApplicants === 'function') {
      onSyncApplicants();
      return;
    }
    if (!sessionId) {
      setSyncing(false);
      return;
    }
    try {
      const meta = getMetadata();
      const cid = meta?.cid || meta?.companyId;
      aiAutomationApi.post('/get-postedjob-candidates', { session_id: sessionId, cid: cid ? parseInt(cid) : undefined }, { timeout: 300000 }).catch(() => {});
      setToastMessage('Sync started — track progress in sourcing'); setToastType('success'); setShowToast(true);
    } catch { setSyncing(false); setToastMessage('Failed to start sync'); setToastType('error'); setShowToast(true); }
  };

  const formatLastSync = (date) => {
    if (!date) return null;
    const safeDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(safeDate.getTime())) return null;
    const d = Math.floor((Date.now() - safeDate.getTime()) / 1000);
    if (d < 60) return 'Just now';
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  };

  const effectiveHasJobPosted = hasPersistedJobPosted || normalizedPostedChannels.length > 0;
  const effectivePostedPlatform = (() => {
    const hasNaukri = normalizedPostedChannels.includes('naukri') || persistedPostedPlatform === 'naukri' || persistedPostedPlatform === 'both';
    const hasLinkedin = normalizedPostedChannels.includes('linkedin') || persistedPostedPlatform === 'linkedin' || persistedPostedPlatform === 'both';
    if (hasNaukri && hasLinkedin) return 'both';
    if (hasNaukri) return 'naukri';
    if (hasLinkedin) return 'linkedin';
    return '';
  })();

  const getSyncLabel = () => {
    if (effectivePostedPlatform === 'both') return 'Sync Naukri & LinkedIn';
    if (effectivePostedPlatform === 'naukri') return 'Sync Naukri';
    if (effectivePostedPlatform === 'linkedin') return 'Sync LinkedIn';
    return 'Sync Applicants';
  };

  // ─── Derived Data ───────────────────────────────────────────────────────────

  const localFilteredCandidates = candidates.filter((candidate) => candidateMatchesFilters(candidate, debouncedSearchTerm, sourceFilter, statusFilter));
  const paginatedCandidates = sessionId
    ? serverCandidates
    : localFilteredCandidates.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalCandidatePages = sessionId ? totalPages : Math.max(1, Math.ceil(localFilteredCandidates.length / ITEMS_PER_PAGE));
  const activeFilterCount = sourceFilter.length + statusFilter.length;
  const displayedTotalRecords = sessionId
    ? totalRecords
    : localFilteredCandidates.length;
  const totalProfiles = sessionId
    ? Number(totalRecords || 0)
    : Number(sourcingStats.total_profiles || candidates.length || 0);
  const emailCount = sessionId
    ? Number(serverEmailCount !== null ? serverEmailCount : paginatedCandidates.filter(c => c.email?.toString().trim()).length)
    : Number(sourcingStats.email_count || paginatedCandidates.filter(c => c.email?.toString().trim()).length || 0);
  const normalizeStatusCountMap = (counts = {}) => Object.entries(counts || {}).reduce((acc, [key, value]) => {
    const statusKey = getCandidateStatusKey({ funnel_stage: key });
    acc[statusKey] = (acc[statusKey] || 0) + Number(value || 0);
    return acc;
  }, {});
  const shortlistSummaryCandidates = sessionId ? serverCandidates : localFilteredCandidates;
  const pageStatusCounts = shortlistSummaryCandidates.reduce((acc, candidate) => {
    const statusKey = getCandidateStatusKey(candidate);
    acc[statusKey] = (acc[statusKey] || 0) + 1;
    return acc;
  }, {});
  const shortlistStatusCounts = sessionId && Object.keys(serverStatusCounts || {}).length > 0
    ? normalizeStatusCountMap(serverStatusCounts)
    : pageStatusCounts;
  const shortlistStatusSummary = [
    { key: 'imported', label: 'Imported' },
    { key: 'identified', label: 'Sourced' },
    { key: 'contacted', label: 'Contacted' },
    { key: 'interested', label: 'Interested' },
    { key: 'shortlisted', label: 'Shortlisted' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'interviewed', label: 'Interviewed' },
    { key: 'declined', label: 'Rejected' },
    { key: 'hired', label: 'Hired' },
  ].filter(({ key }) => Number(shortlistStatusCounts[key] || 0) > 0);

  useEffect(() => {
    if (!selectedCandidates.length) return;
    const validSelectionKeys = new Set(
      [...candidates, ...serverCandidates, ...paginatedCandidates].map((candidate, index) =>
        getCandidateSelectionKey(candidate, index),
      ),
    );
    setSelectedCandidates((prev) => prev.filter((candidateKey) => validSelectionKeys.has(candidateKey)));
  }, [candidates, serverCandidates, paginatedCandidates, selectedCandidates.length, getCandidateSelectionKey]);

  const selectedSourcingChannelList = (() => {
    const rawSelectedChannels = Array.isArray(selectedSourcingChannels)
      ? selectedSourcingChannels
      : (selectedSourcingChannels ? [selectedSourcingChannels] : []);
    return rawSelectedChannels
      .map(normalizeSourcingChannel)
      .filter(Boolean);
  })();
  const filteredSourcingChannelList = (() => {
    const rawChannels = Array.isArray(channelFilter)
      ? channelFilter
      : (channelFilter ? [channelFilter] : []);
    return rawChannels
      .map(normalizeSourcingChannel)
      .filter(Boolean);
  })();
  const hasExplicitSourcingSelection = selectedSourcingChannelList.length > 0 || filteredSourcingChannelList.length > 0;
  const configuredSourcingChannels = (() => {
    if (selectedSourcingChannelList.length > 0) return new Set(selectedSourcingChannelList);
    if (filteredSourcingChannelList.length > 0) return new Set(filteredSourcingChannelList);
    if (showSourcingLogs) return new Set(['google', 'linkedin', 'resumebox']);
    return new Set();
  })();
  const getSourcingWorkflowRow = (channel) => findWorkflowSnapshot(workflowSnapshot, 'sourcing', channel);
  const googleWorkflowRow = getSourcingWorkflowRow('google');
  const linkedinWorkflowRow = getSourcingWorkflowRow('linkedin');
  const resumeBoxWorkflowRow = getSourcingWorkflowRow('resumebox');
  const googleWorkflowStatus = dbProcessStatus?.sourcing_google || workflowStateToLegacyStatus(googleWorkflowRow);
  const linkedinWorkflowStatus = dbProcessStatus?.sourcing_linkedin || workflowStateToLegacyStatus(linkedinWorkflowRow);
  const resumeBoxWorkflowStatus = dbProcessStatus?.sourcing_resumebox || workflowStateToLegacyStatus(resumeBoxWorkflowRow);
  const workflowHasTerminalState = ['complete', 'failed'].includes(googleWorkflowStatus)
    || ['complete', 'failed'].includes(linkedinWorkflowStatus)
    || ['complete', 'failed'].includes(resumeBoxWorkflowStatus);
  const latestSourcingTimestamp = [...googleLogs, ...linkedinLogs, ...resumeBoxLogs, googleWorkflowRow, linkedinWorkflowRow, resumeBoxWorkflowRow]
    .map((entry) => (typeof entry === 'string' ? null : (entry?.timestamp || entry?.created_at || entry?.updated_at || null)))
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;
  const sourcingRunCompleted = !googleProcessing
    && !linkedinProcessing
    && !resumeBoxProcessing
    && (
      googleLogs.length > 0
      || linkedinLogs.length > 0
      || resumeBoxLogs.length > 0
      || googleSearchFoundCount > 0
      || linkedinSearchFoundCount > 0
      || googleCandidateCount > 0
      || linkedinCandidateCount > 0
      || resumeBoxCandidateCount > 0
      || matchedCandidateCount > 0
      || enrichedCandidateCount > 0
      || totalProfiles > 0
      || workflowHasTerminalState
    );

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectCandidate = (id) => setSelectedCandidates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAll = () => {
    const ids = paginatedCandidates.map((c, i) => getCandidateSelectionKey(c, i));
    setSelectedCandidates(prev => prev.length === ids.length ? [] : ids);
  };
  const handleToggleExpand = (id) => setExpandedCandidateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleViewProfile = (candidate) => {
    onViewCandidate?.(candidate);
  };

  const handleStartOutreachForSelected = () => {
    if (!selectedCandidates.length) { setToastMessage('Select at least one candidate'); setToastType('warning'); setShowToast(true); return; }
    const candidateLookup = new Map();
    [...candidates, ...serverCandidates, ...paginatedCandidates].forEach((candidate, index) => {
      const key = getCandidateSelectionKey(candidate, index);
      if (!candidateLookup.has(key)) candidateLookup.set(key, candidate);
    });
    const sel = selectedCandidates
      .map((id) => candidateLookup.get(id))
      .filter(Boolean);
    onStartOutreach?.({ candidateIds: selectedCandidates, candidates: sel });
  };
  const handleStartOutreachForAll = () => {
    const ids = paginatedCandidates.map((c, i) => getCandidateSelectionKey(c, i));
    setSelectedCandidates(ids);
    onStartOutreach?.({ candidateIds: ids, candidates: paginatedCandidates });
  };

  // ─── Shared Styles ──────────────────────────────────────────────────────────

  const card = {
    background: isDark ? '#1F2937' : '#fff',
    border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
    borderRadius: 12,
    overflow: 'hidden',
  };

  const textPrimary = { color: isDark ? '#F9FAFB' : '#111827' };
  const textMuted = { color: isDark ? '#9CA3AF' : '#6B7280' };
  const sourcingListTheme = {
    surface: isDark ? '#0F172A' : '#FAFBFF',
    card: isDark ? '#111827' : '#FFFFFF',
    selected: isDark ? 'rgba(59, 91, 219, 0.18)' : '#F7F8FF',
    hover: isDark ? '#1E293B' : '#F5F3FF',
    border: isDark ? '#334155' : '#ECEEF8',
    hoverBorder: isDark ? '#64748B' : '#C7D2FE',
    separator: isDark ? '#1F2937' : '#E5E7EB',
    controlBorder: isDark ? '#64748B' : '#D1D5DB',
    emailBg: isDark ? 'rgba(251, 191, 36, 0.14)' : '#FEF3C7',
    emailText: isDark ? '#FCD34D' : '#92400E',
    emailBorder: isDark ? 'rgba(251, 191, 36, 0.28)' : '#FDE68A',
    locationBg: isDark ? 'rgba(248, 113, 113, 0.12)' : '#FEF2F2',
    locationText: isDark ? '#FCA5A5' : '#A32D2D',
    locationBorder: isDark ? 'rgba(248, 113, 113, 0.26)' : '#FECACA',
    expBg: isDark ? 'rgba(34, 197, 94, 0.12)' : '#F0FDF4',
    expText: isDark ? '#86EFAC' : '#15803D',
    expBorder: isDark ? 'rgba(34, 197, 94, 0.26)' : '#BBF7D0',
  };

  const filterPillStyle = (active) => ({
    fontSize: 12,
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${active ? primaryColor : (isDark ? '#374151' : '#E5E7EB')}`,
    background: active ? hexToRgba(primaryColor, 0.08) : 'transparent',
    color: active ? primaryColor : (isDark ? '#9CA3AF' : '#6B7280'),
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontWeight: active ? 600 : 400,
  });

  // ─── Render ─────────────────────────────────────────────────────────────────

  const buildSourcingCard = ({
    key,
    title,
    icon,
    logs,
    processing,
    candidateCount,
    defaultMetric,
    zeroResultSummary,
  }) => {
    const isConfigured = configuredSourcingChannels.has(key);
    const workflowRow = getSourcingWorkflowRow(key);
    const workflowStatus = dbProcessStatus?.[`sourcing_${key}`] || workflowStateToLegacyStatus(workflowRow);
    const workflowFoundCount = workflowCount(workflowRow, ['candidate_count', 'profile_count', 'found_count']);
    const effectiveCandidateCount = Number(candidateCount) || workflowFoundCount || 0;
    const workflowLog = workflowStatus && workflowStatus !== 'not_started'
      ? workflowSyntheticLog(workflowRow, `${title} status updated.`)
      : null;
    const effectiveLogs = Array.isArray(logs) && logs.length > 0
      ? logs
      : (workflowLog ? [workflowLog] : []);
    const latestLog = effectiveLogs.length > 0 ? effectiveLogs[effectiveLogs.length - 1] : null;
    const latestStatus = typeof latestLog === 'string'
      ? ''
      : String(latestLog?.status || latestLog?.logType || latestLog?.type || '').toLowerCase();
    const latestMessage = typeof latestLog === 'string'
      ? latestLog
      : String(latestLog?.message || latestLog?.log_message || latestLog?.status || '');
    const latestMessageLower = latestMessage.toLowerCase();
    const logIsFailureTerminal =
      ['failed', 'error'].includes(latestStatus)
      || latestMessageLower.includes('failed')
      || latestMessageLower.includes('error');
    const dbTerminalStatus = workflowStatus === 'complete' || workflowStatus === 'failed';
    const logLooksLikeFinalCompletion =
      ['completed', 'complete'].includes(latestStatus)
      || latestMessageLower.includes('candidate sourcing completed')
      || latestMessageLower.includes('sourcing completed')
      || latestMessageLower.includes('search completed - no candidates found')
      || latestMessageLower.includes('no candidates found')
      || latestMessageLower.includes('no google candidates found')
      || latestMessageLower.includes('no linkedin candidates found')
      || latestMessageLower.includes('no matching candidates found');
    const logIsSuccessTerminal = dbTerminalStatus || logLooksLikeFinalCompletion;
    const logIsTerminal =
      logIsFailureTerminal
      || logIsSuccessTerminal
      || workflowStatus === 'complete'
      || workflowStatus === 'failed';
    const effectiveProcessing = (Boolean(processing) || workflowStatus === 'processing') && !logIsTerminal;
    const hasRealActivity = effectiveLogs.length > 0 || effectiveProcessing || effectiveCandidateCount > 0;
    const canUseRunCompletion = isConfigured && (hasExplicitSourcingSelection || hasRealActivity);
    const channelCompleted = logIsTerminal || effectiveCandidateCount > 0 || (canUseRunCompletion && sourcingRunCompleted);
    if (!isConfigured && hasExplicitSourcingSelection) return null;
    if (!isConfigured && !hasRealActivity) return null;

    const zeroResultLogs = !hasRealActivity && channelCompleted
      ? [{
          message: zeroResultSummary,
          status: 'completed',
          timestamp: latestSourcingTimestamp || undefined,
        }]
      : [];

    return {
      key,
      title,
      icon,
      metric: effectiveCandidateCount > 0
        ? `${effectiveCandidateCount} profile${effectiveCandidateCount === 1 ? '' : 's'} found`
        : workflowRow?.status_label
        ? workflowRow.status_label
        : (channelCompleted && isConfigured ? '0 profiles found' : defaultMetric),
      logs: effectiveLogs.length > 0 ? effectiveLogs : zeroResultLogs,
      processing: effectiveProcessing,
      statusOverride: workflowStatus === 'failed' || logIsFailureTerminal
        ? 'failed'
        : workflowStatus === 'complete' || logIsSuccessTerminal || (!effectiveProcessing && channelCompleted)
        ? 'success'
        : null,
      completedLabel: 'Completed',
      idleLabel: isConfigured ? 'Ready' : 'Queued',
      emptySummary: channelCompleted && isConfigured ? zeroResultSummary : 'This source has not started yet.',
    };
  };

  const sourcingProcessCards = [
    buildSourcingCard({
      key: 'google',
      title: 'Online Sourcing',
      icon: 'G',
      logs: googleLogs,
      processing: googleProcessing,
      candidateCount: googleCandidateCount,
      defaultMetric: 'Web candidate discovery',
      zeroResultSummary: 'No candidates found from online sourcing in this run.',
    }),
    buildSourcingCard({
      key: 'linkedin',
      title: 'LinkedIn Sourcing',
      icon: 'in',
      logs: linkedinLogs,
      processing: linkedinProcessing,
      candidateCount: linkedinCandidateCount,
      defaultMetric: 'Professional profile discovery',
      zeroResultSummary: 'No LinkedIn candidates found in this run.',
    }),
    buildSourcingCard({
      key: 'resumebox',
      title: RESUME_LIBRARY_LABEL,
      icon: 'R',
      logs: resumeBoxLogs,
      processing: resumeBoxProcessing,
      candidateCount: resumeBoxCandidateCount,
      defaultMetric: 'Candidate library check',
      zeroResultSummary: 'No matching candidates found in the resume library.',
    }),
  ].filter(Boolean);
  const sourcingActivityUpdates = sourcingProcessCards.reduce((sum, card) => sum + (card.logs?.length || 0), 0);
  const sourcingActivityProcessing = sourcingProcessCards.some((card) => card.processing);
  const applicantSyncLastUpdatedAt = useMemo(() => {
    const localTimestamp = lastSyncTime instanceof Date ? lastSyncTime : (lastSyncTime ? new Date(lastSyncTime) : null);
    const externalTimestamp = lastApplicantsSyncTime instanceof Date
      ? lastApplicantsSyncTime
      : (lastApplicantsSyncTime ? new Date(lastApplicantsSyncTime) : null);

    const localMs = localTimestamp && !Number.isNaN(localTimestamp.getTime()) ? localTimestamp.getTime() : null;
    const externalMs = externalTimestamp && !Number.isNaN(externalTimestamp.getTime()) ? externalTimestamp.getTime() : null;

    if (localMs && externalMs) {
      return new Date(Math.max(localMs, externalMs));
    }

    return externalMs ? externalTimestamp : localTimestamp;
  }, [lastApplicantsSyncTime, lastSyncTime]);
  const applicantSyncing = syncing || Boolean(syncingApplicants);
  const normalMailSyncAvailable = typeof onSyncReplies === 'function';
  const emailSyncAvailable = Boolean(normalMailSyncAvailable || subjectMailSyncEnabled);
  const visibleApplicantSyncing = showApplicantSync && applicantSyncing;
  const showUnifiedSyncCard =
    (showApplicantSync && (effectiveHasJobPosted || applicantSyncing || Boolean(applicantSyncLastUpdatedAt))) ||
    (emailSyncAvailable && (Boolean(searchId) || emailSyncing || emailSyncPhase === 'complete' || emailSyncPhase === 'error' || emailSyncPhase === 'auth_required' || emailSyncLogs.length > 0));
  const emailSyncDisplayLogs = emailSyncLogs.length > 0
    ? emailSyncLogs
    : (emailSyncCompletedAt
        ? [{
            message: 'Email sync completed',
            status: 'completed',
            timestamp: emailSyncCompletedAt instanceof Date ? emailSyncCompletedAt.toISOString() : emailSyncCompletedAt,
          }]
        : []);
  const emailSyncReplyLogs = (() => {
    const logs = [];
    const summaries = Array.isArray(emailSyncResult.reply_summaries) ? emailSyncResult.reply_summaries : [];
    summaries.forEach((reply) => {
      const email = reply.candidate_email || 'Candidate';
      const subject = reply.subject || 'No subject';
      const label = reply.classification || 'reply';
      const preview = reply.preview ? ` — ${reply.preview}` : '';
      logs.push({
        message: `${email} replied: ${subject} (${label})${preview}`,
        timestamp: reply.received_at || emailSyncCompletedAt || emailSyncState.updatedAt || '',
        logType: 'info',
      });
    });
    if (logs.length === 0 && emailSyncResult.all_replies && typeof emailSyncResult.all_replies === 'object') {
      Object.entries(emailSyncResult.all_replies).slice(0, 20).forEach(([email, replies]) => {
        const list = Array.isArray(replies) ? replies : [];
        if (list.length === 0) return;
        const latest = list[list.length - 1] || {};
        logs.push({
          message: `${email} replied${latest.subject ? `: ${latest.subject}` : ''}${latest.classification ? ` (${latest.classification})` : ''}`,
          timestamp: latest.received_at || latest.created_at || emailSyncCompletedAt || '',
          logType: 'info',
        });
      });
    }
    return logs;
  })();
  const sourcingActivityCards = sourcingProcessCards;
  const sourcingActivityTotalUpdates = sourcingActivityUpdates;
  const sourcingActivityHasAnyCard = sourcingActivityCards.length > 0;
  const sourcingActivityIsProcessing = sourcingActivityProcessing;
  const sourcingStatusChips = sourcingActivityCards.map((process) => ({
    key: process.key,
    label: process.title,
    tone: process.processing ? 'active' : ((process.logs?.length || 0) > 0 ? 'done' : 'idle'),
    detail: process.processing
      ? 'Processing'
      : (process.metric || `${process.logs?.length || 0} updates`),
  }));
  const sourcingLatestActivity = [...sourcingActivityCards]
    .reverse()
    .find((process) => Array.isArray(process.logs) && process.logs.length > 0);
  const sourcingLatestSummary = sourcingLatestActivity
    ? (() => {
        const latestLog = sourcingLatestActivity.logs[sourcingLatestActivity.logs.length - 1];
        const message = typeof latestLog === 'string'
          ? latestLog
          : (latestLog?.message || latestLog?.logMessage || '');
        return message || sourcingLatestActivity.metric || '';
      })()
    : '';
  const sourcingLastUpdatedLabel = latestSourcingTimestamp
    ? formatLastSync(latestSourcingTimestamp)
    : null;
  const candidateSyncLastUpdatedAt = (showApplicantSync ? applicantSyncLastUpdatedAt : null)
    || emailSyncState.updatedAt
    || emailSyncCompletedAt
    || emailSyncState.startedAt
    || processTrackingStats.lastSync
    || null;
  const candidateSyncLastUpdatedLabel = candidateSyncLastUpdatedAt
    ? formatLastSync(candidateSyncLastUpdatedAt)
    : null;
  const emailSyncNewCount = Number(emailSyncResult.new_count || 0);
  const emailSyncResumeParsed = Number(emailSyncResult.resumes_parsed || emailSyncResult.parsed_resumes || emailSyncState.resumeParsed || processTrackingStats.parsedResumes || 0);
  const emailSyncResumeTotal = Number(emailSyncResult.resumes_total || emailSyncState.resumeTotal || emailSyncResumeParsed || 0);
  const emailSyncTotalMails = Number(emailSyncResult.total_mails || processTrackingStats.totalMails || emailSyncNewCount || 0);
  const emailSyncDuplicates = Number(processTrackingStats.duplicateResumes || emailSyncResult.duplicate_resumes || 0);
  const emailLastSyncAt = emailSyncState.lastSync || emailSyncCompletedAt || processTrackingStats.lastSync || lastSyncTime || null;
  const emailLastUpdatedAt = emailSyncState.updatedAt || emailLastSyncAt || emailSyncState.startedAt || null;
  const emailSyncCombinedLogs = [
    ...emailSyncDisplayLogs,
    ...emailSyncReplyLogs,
    ...subjectSyncLogs.map((entry) => (typeof entry === 'string' ? entry : ({
      message: entry.message || '',
      timestamp: entry.timestamp || '',
      logType: entry.logType || entry.status || entry.phase || 'info',
    }))),
  ];
  const emailSyncFileLogs = (fileLogsState['email-sync']?.logs || []).map((entry) => ({
    message: entry.message || '',
    timestamp: entry.timestamp || '',
    logType: entry.log_type || entry.logType || 'info',
  }));
  const emailSyncFullLogs = [...emailSyncFileLogs, ...emailSyncCombinedLogs].filter((entry, index, entries) => {
    const message = typeof entry === 'string' ? entry : entry.message || entry.logMessage || '';
    const timestamp = typeof entry === 'string' ? '' : entry.timestamp || '';
    return entries.findIndex((candidate) => {
      const candidateMessage = typeof candidate === 'string' ? candidate : candidate.message || candidate.logMessage || '';
      const candidateTimestamp = typeof candidate === 'string' ? '' : candidate.timestamp || '';
      return candidateMessage === message && candidateTimestamp === timestamp;
    }) === index;
  });
  const emailSyncLatestMessage = typeof emailSyncDisplayLogs[emailSyncDisplayLogs.length - 1] === 'string'
    ? emailSyncDisplayLogs[emailSyncDisplayLogs.length - 1]
    : (emailSyncDisplayLogs[emailSyncDisplayLogs.length - 1]?.message
      || emailSyncDisplayLogs[emailSyncDisplayLogs.length - 1]?.logMessage
      || '');
  const emailSyncSummary = (() => {
    if (emailSyncing) {
      if (emailSyncState.resumeParsing && emailSyncResumeTotal > 0) {
        return `Parsing resumes · ${emailSyncResumeParsed}/${emailSyncResumeTotal} completed`;
      }
      return emailSyncLatestMessage || 'Sync in progress';
    }
    if (emailSyncPhase === 'error') {
      return emailSyncLatestMessage || 'Sync failed';
    }
    if (emailSyncPhase === 'auth_required') {
      return emailSyncLatestMessage || 'Mail authorization required. Waiting for reconnect...';
    }
    if (emailSyncPhase === 'complete' || emailSyncCompletedAt) {
      const parts = [];
      parts.push(emailSyncTotalMails > 0 ? `${emailSyncTotalMails} mail${emailSyncTotalMails === 1 ? '' : 's'} found` : 'No new mails');
      if (emailSyncResumeTotal > 0) {
        parts.push(`${emailSyncResumeParsed}/${emailSyncResumeTotal} resumes parsed`);
      } else if (emailSyncResumeParsed > 0) {
        parts.push(`${emailSyncResumeParsed} resume${emailSyncResumeParsed === 1 ? '' : 's'} parsed`);
      }
      if (emailSyncDuplicates > 0) {
        parts.push(`${emailSyncDuplicates} duplicate${emailSyncDuplicates === 1 ? '' : 's'}`);
      }
      return parts.join(' · ');
    }
    return 'Sync mail replies into candidate updates';
  })();
  const sectionSurface = {
    ...card,
    borderRadius: 14,
    background: isDark ? '#111827' : '#FFFFFF',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
    boxShadow: 'none',
  };
  const secondaryButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    padding: '7px 11px',
    borderRadius: 8,
    border: `1px solid ${isDark ? '#374151' : '#D7DFEA'}`,
    background: isDark ? '#111827' : '#fff',
    color: isDark ? '#D1D5DB' : '#374151',
    cursor: 'pointer',
  };
  const applicantSyncStatus = applicantSyncing ? 'Syncing' : applicantSyncLastUpdatedAt ? 'Synced' : effectiveHasJobPosted ? 'Ready' : 'Unavailable';
  const emailSyncStatus = emailSyncPhase === 'complete' ? 'Synced' : emailSyncPhase === 'auth_required' ? 'Authorization needed' : emailSyncPhase === 'error' ? 'Failed' : emailSyncing ? 'Syncing' : 'Ready';
  return (
    <>
      {subjectMailAuthPrompt?.authUrl && (
        <div
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 10000,
            width: 'min(420px, calc(100vw - 32px))',
            borderRadius: 14,
            border: `1px solid ${isDark ? '#334155' : '#D7DFEA'}`,
            background: isDark ? '#0F172A' : '#FFFFFF',
            boxShadow: isDark ? '0 18px 48px rgba(0, 0, 0, 0.45)' : '0 18px 48px rgba(15, 23, 42, 0.18)',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                Connect {subjectMailAuthPrompt.providerLabel || 'Mail Provider'}
              </div>
              <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', marginTop: 6, lineHeight: 1.45 }}>
                Mail sync needs authorization. After connecting, the subject sync will continue automatically.
              </div>
              {subjectMailAuthPrompt.error && (
                <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>
                  {subjectMailAuthPrompt.error}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSubjectMailAuthPrompt(null)}
              aria-label="Close mail authorization prompt"
              style={{
                border: 'none',
                background: isDark ? '#1E293B' : '#F1F5F9',
                color: isDark ? '#CBD5E1' : '#475569',
                width: 28,
                height: 28,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: '28px',
              }}
            >
              ×
            </button>
          </div>
          <button
            type="button"
            onClick={handleSubjectMailAuthConnect}
            disabled={subjectMailAuthPrompt.connecting || subjectSyncLoading}
            style={{
              marginTop: 14,
              width: '100%',
              border: 'none',
              borderRadius: 10,
              padding: '10px 12px',
              background: subjectMailAuthPrompt.connecting || subjectSyncLoading ? '#94A3B8' : primaryColor,
              color: '#FFFFFF',
              fontWeight: 800,
              cursor: subjectMailAuthPrompt.connecting || subjectSyncLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {subjectMailAuthPrompt.connecting ? 'Connecting...' : `Connect ${subjectMailAuthPrompt.providerLabel || 'Mail'}`}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* ── Header bar ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 14,
            background: isDark ? '#0F172A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
          }}
        >
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, ...textPrimary, margin: 0 }}>Shortlisted Candidates</h3>
            <p style={{ fontSize: 13, ...textMuted, margin: '2px 0 0' }}>
              {totalProfiles} candidates · {emailCount} emails
            </p>
            {shortlistStatusSummary.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {shortlistStatusSummary.map(({ key, label }) => (
                  <span
                    key={key}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 9px',
                      borderRadius: 999,
                      background: isDark ? '#111827' : '#F8FAFC',
                      border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
                      color: isDark ? '#CBD5E1' : '#475569',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {label} · {shortlistStatusCounts[key]}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setScoreConfigOpen(true)}
              disabled={!effectiveJdId}
              style={{
                ...secondaryButtonStyle,
                cursor: effectiveJdId ? 'pointer' : 'not-allowed',
                opacity: effectiveJdId ? 1 : 0.55,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20v-6M6 20V10M18 20V4"/></svg>
              Score config
            </button>
            {/* Refresh */}
            <button
              onClick={handleRefreshCandidates}
              disabled={refreshing}
              style={{ ...secondaryButtonStyle, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h5M20 20v-5h-5M4.22 14a8 8 0 0014.56 0M19.78 10a8 8 0 00-14.56 0"/></svg>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {showUnifiedSyncCard && (
          <div
            style={{
              ...sectionSurface,
              padding: '10px 12px',
              background: isDark
                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)',
              borderColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(226, 232, 240, 0.95)',
              boxShadow: isDark ? '0 10px 28px rgba(2, 6, 23, 0.24)' : '0 10px 30px rgba(15, 23, 42, 0.06)',
            }}
          >
            <button
              type="button"
              onClick={() => setCandidateSyncCollapsed((prev) => !prev)}
              style={{
                width: '100%',
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: '1 1 280px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                      Candidate Sync
                    </div>
                    <span
                      style={{
                        padding: '4px 9px',
                        borderRadius: 999,
                        background: visibleApplicantSyncing
                          ? (isDark ? 'rgba(96,165,250,0.2)' : 'rgba(219,234,254,0.92)')
                          : candidateSyncLastUpdatedAt
                          ? (isDark ? 'rgba(45,212,191,0.18)' : 'rgba(209,250,229,0.92)')
                          : (isDark ? 'rgba(71,85,105,0.28)' : 'rgba(241,245,249,0.96)'),
                        color: visibleApplicantSyncing
                          ? '#2563EB'
                          : candidateSyncLastUpdatedAt
                          ? '#047857'
                          : (isDark ? '#CBD5E1' : '#475569'),
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {visibleApplicantSyncing ? 'Applicant sync processing' : candidateSyncLastUpdatedAt ? 'Synced' : 'Ready'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {showApplicantSync && (
                      <span style={{ fontSize: 11, color: applicantSyncing ? '#1D4ED8' : applicantSyncLastUpdatedAt ? '#4338CA' : (isDark ? '#C7D2FE' : '#3730A3'), background: applicantSyncing ? (isDark ? 'rgba(59,130,246,0.18)' : 'rgba(219,234,254,0.96)') : applicantSyncLastUpdatedAt ? (isDark ? 'rgba(67,56,202,0.18)' : 'rgba(238,242,255,0.96)') : (isDark ? 'rgba(30,41,59,0.82)' : 'rgba(245,247,255,0.98)'), border: `1px solid ${applicantSyncing ? (isDark ? 'rgba(96,165,250,0.34)' : 'rgba(147,197,253,0.95)') : applicantSyncLastUpdatedAt ? (isDark ? 'rgba(129,140,248,0.34)' : 'rgba(199,210,254,0.98)') : (isDark ? 'rgba(99,102,241,0.26)' : 'rgba(224,231,255,0.98)')}`, borderRadius: 999, padding: '4px 8px', fontWeight: 700 }}>
                        Applicant Sync · {applicantSyncStatus}
                      </span>
                    )}
                    {normalMailSyncAvailable && (
                      <span style={{ fontSize: 11, color: emailSyncing ? '#0F766E' : emailSyncPhase === 'complete' ? '#0F766E' : emailSyncPhase === 'error' ? '#B91C1C' : (isDark ? '#99F6E4' : '#115E59'), background: emailSyncing ? (isDark ? 'rgba(13,148,136,0.18)' : 'rgba(204,251,241,0.98)') : emailSyncPhase === 'complete' ? (isDark ? 'rgba(15,118,110,0.18)' : 'rgba(209,250,229,0.98)') : emailSyncPhase === 'error' ? (isDark ? 'rgba(239,68,68,0.14)' : 'rgba(254,242,242,0.96)') : (isDark ? 'rgba(17,24,39,0.82)' : 'rgba(236,253,245,0.98)'), border: `1px solid ${emailSyncing ? (isDark ? 'rgba(45,212,191,0.34)' : 'rgba(153,246,228,0.98)') : emailSyncPhase === 'complete' ? (isDark ? 'rgba(45,212,191,0.28)' : 'rgba(167,243,208,0.98)') : emailSyncPhase === 'error' ? (isDark ? 'rgba(248,113,113,0.22)' : 'rgba(252,165,165,0.95)') : (isDark ? 'rgba(20,184,166,0.24)' : 'rgba(167,243,208,0.98)')}`, borderRadius: 999, padding: '4px 8px', fontWeight: 700 }}>
                        Mail Sync · {emailSyncStatus}
                      </span>
                    )}
                    {subjectMailSyncEnabled && (
                      <span style={{ fontSize: 11, color: emailSyncing ? '#0F766E' : emailSyncPhase === 'complete' ? '#0F766E' : emailSyncPhase === 'error' ? '#B91C1C' : (isDark ? '#99F6E4' : '#115E59'), background: emailSyncing ? (isDark ? 'rgba(13,148,136,0.18)' : 'rgba(204,251,241,0.98)') : emailSyncPhase === 'complete' ? (isDark ? 'rgba(15,118,110,0.18)' : 'rgba(209,250,229,0.98)') : emailSyncPhase === 'error' ? (isDark ? 'rgba(239,68,68,0.14)' : 'rgba(254,242,242,0.96)') : (isDark ? 'rgba(17,24,39,0.82)' : 'rgba(236,253,245,0.98)'), border: `1px solid ${emailSyncing ? (isDark ? 'rgba(45,212,191,0.34)' : 'rgba(153,246,228,0.98)') : emailSyncPhase === 'complete' ? (isDark ? 'rgba(45,212,191,0.28)' : 'rgba(167,243,208,0.98)') : emailSyncPhase === 'error' ? (isDark ? 'rgba(248,113,113,0.22)' : 'rgba(252,165,165,0.95)') : (isDark ? 'rgba(20,184,166,0.24)' : 'rgba(167,243,208,0.98)')}`, borderRadius: 999, padding: '4px 8px', fontWeight: 700 }}>
                        Mail Sync Based on Subject · {emailSyncStatus}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#64748B', marginTop: 8, lineHeight: 1.4 }}>
                    {candidateSyncLastUpdatedLabel
                      ? `Last synced ${candidateSyncLastUpdatedLabel}`
                      : visibleApplicantSyncing
                      ? 'Applicant sync in progress'
                      : emailSyncing
                      ? 'Mail sync in progress'
                      : 'No sync yet'}
                  </div>
                </div>
                <span
                  style={{
                    ...secondaryButtonStyle,
                    flexShrink: 0,
                  }}
                >
                  {candidateSyncCollapsed ? 'View details' : 'Hide details'}
                </span>
              </div>
            </button>

            {!candidateSyncCollapsed && (
              <>
            <div style={{ display: 'grid', gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}` }}>
              {showApplicantSync && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 16px', borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}`, borderRadius: 16, background: isDark ? 'linear-gradient(135deg, rgba(30,41,59,0.7) 0%, rgba(49,46,129,0.18) 100%)' : 'linear-gradient(135deg, rgba(239,246,255,0.96) 0%, rgba(238,242,255,0.9) 100%)', border: `1px solid ${isDark ? 'rgba(99,102,241,0.18)' : 'rgba(199,210,254,0.9)'}` }}>
                  <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                        Applicant Sync
                      </div>
                    <span
                      style={{
                        padding: '4px 9px',
                        borderRadius: 999,
                        background: applicantSyncing
                          ? (isDark ? 'rgba(59,130,246,0.18)' : 'rgba(219,234,254,0.96)')
                          : applicantSyncLastUpdatedAt
                          ? (isDark ? 'rgba(34,197,94,0.18)' : 'rgba(220,252,231,0.96)')
                          : (isDark ? 'rgba(100,116,139,0.18)' : 'rgba(241,245,249,0.96)'),
                        color: applicantSyncing ? '#1D4ED8' : applicantSyncLastUpdatedAt ? '#15803D' : (isDark ? '#CBD5E1' : '#475569'),
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {applicantSyncStatus}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', marginTop: 4, lineHeight: 1.4 }}>
                    {applicantSyncing
                      ? 'Fetching latest applicants from Naukri and LinkedIn.'
                      : applicantSyncLastUpdatedAt
                      ? `Last synced ${formatLastSync(applicantSyncLastUpdatedAt)}`
                      : effectiveHasJobPosted
                      ? 'Sync Naukri and LinkedIn applicants into the candidate list.'
                      : 'Post the job first to enable applicant sync.'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={handleSyncPostedJob}
                      disabled={applicantSyncing || !effectiveHasJobPosted}
                      style={{
                        ...secondaryButtonStyle,
                        borderColor: applicantSyncing || applicantSyncLastUpdatedAt ? (isDark ? 'rgba(129,140,248,0.32)' : 'rgba(165,180,252,0.95)') : secondaryButtonStyle.borderColor,
                        background: applicantSyncing || !effectiveHasJobPosted
                          ? (isDark ? 'rgba(51, 65, 85, 0.45)' : 'rgba(248, 250, 252, 0.85)')
                        : (isDark ? 'rgba(49,46,129,0.2)' : '#F7F9FF'),
                        cursor: applicantSyncing || !effectiveHasJobPosted ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {applicantSyncing ? 'Syncing…' : getSyncLabel()}
                  </button>
                </div>
                </div>
              )}

              {emailSyncAvailable && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 16px', borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}`, borderRadius: 16, background: isDark ? 'linear-gradient(135deg, rgba(6,78,59,0.3) 0%, rgba(15,118,110,0.18) 100%)' : 'linear-gradient(135deg, rgba(236,253,245,0.96) 0%, rgba(240,253,250,0.98) 100%)', border: `1px solid ${isDark ? 'rgba(45,212,191,0.18)' : 'rgba(167,243,208,0.95)'}` }}>
                  <div style={{ minWidth: 0, flex: '1 1 360px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', width: '100%' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                        {normalMailSyncAvailable ? 'Mail Sync' : 'Mail Sync Based on Subject'}
                      </div>
                      <span
                        style={{
                          padding: '4px 9px',
                          borderRadius: 999,
                          background: emailSyncPhase === 'complete'
                            ? (isDark ? 'rgba(15,118,110,0.18)' : 'rgba(209,250,229,0.96)')
                            : emailSyncPhase === 'error'
                            ? (isDark ? 'rgba(239,68,68,0.18)' : 'rgba(254,242,242,0.96)')
                            : (isDark ? 'rgba(13,148,136,0.18)' : 'rgba(204,251,241,0.96)'),
                          color: emailSyncPhase === 'complete' ? '#0F766E' : emailSyncPhase === 'error' ? '#B91C1C' : '#0F766E',
                          fontSize: 11,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {emailSyncStatus}
                      </span>
                      {subjectMailSyncEnabled && (
                        subjectKeyEditing ? (
                          <form
                            onSubmit={(e) => { e.preventDefault(); handleSaveSubjectKey(); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, marginLeft: 'auto' }}
                          >
                            <span style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>Subject:</span>
                            <input
                              type="text"
                              placeholder="React Developer"
                              value={subjectKey}
                              onChange={e => { setSubjectKey(e.target.value); setSubjectKeySaved(false); }}
                              disabled={subjectKeySaving}
                              autoFocus
                              style={{
                                width: 150,
                                fontSize: 11,
                                padding: '3px 6px',
                                borderRadius: 5,
                                border: `1px solid ${isDark ? 'rgba(99,102,241,0.28)' : 'rgba(199,210,254,0.85)'}`,
                                background: isDark ? 'rgba(15,23,42,0.45)' : '#FFFFFF',
                                color: isDark ? '#F1F5F9' : '#0F172A',
                                outline: 'none',
                              }}
                            />
                            <button
                              type="submit"
                              disabled={subjectKeySaving}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: isDark ? '#6EE7B7' : '#047857',
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '2px 3px',
                                cursor: subjectKeySaving ? 'not-allowed' : 'pointer',
                                opacity: subjectKeySaving ? 0.6 : 1,
                              }}
                            >
                              {subjectKeySaving ? 'Saving' : 'Save'}
                            </button>
                          </form>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, marginLeft: 'auto' }}>
                            <span style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>
                              Subject: <strong style={{ color: isDark ? '#CBD5E1' : '#334155' }}>{(subjectKey || '').trim() || 'Not set'}</strong>
                            </span>
                            <button
                              type="button"
                              onClick={() => setSubjectKeyEditing(true)}
                              title="Edit subject keyword"
                              style={{
                                width: 20,
                                height: 20,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                background: 'transparent',
                                color: isDark ? '#A5B4FC' : '#4F46E5',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                              </svg>
                            </button>
                          </span>
                        )
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', marginTop: 4, lineHeight: 1.4 }}>
                      {emailSyncSummary}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                    <span style={{ fontSize: 11, ...textMuted, whiteSpace: 'nowrap' }}>
                      {emailSyncing
                        ? `Updated ${formatLastSync(emailLastUpdatedAt || new Date())}`
                        : (emailLastSyncAt ? `Last synced ${formatLastSync(emailLastSyncAt)}` : 'No sync yet')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveSourcingLog('email-sync')}
                      disabled={!sessionId && emailSyncCombinedLogs.length === 0}
                      style={{
                        ...secondaryButtonStyle,
                        borderColor: emailSyncing || emailSyncPhase === 'complete'
                          ? (isDark ? 'rgba(45,212,191,0.28)' : 'rgba(167,243,208,0.98)')
                          : secondaryButtonStyle.borderColor,
                        background: isDark ? 'rgba(15,118,110,0.16)' : '#F3FFFB',
                        opacity: !sessionId && emailSyncCombinedLogs.length === 0 ? 0.6 : 1,
                        cursor: !sessionId && emailSyncCombinedLogs.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Full log
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (emailSyncing) return;
                        if (normalMailSyncAvailable) {
                          onSyncReplies();
                          if ((subjectKey || '').trim() && subjectMailSyncEnabled) {
                            setTimeout(() => handleSubjectSync(), 0);
                          }
                          return;
                        }
                        if (subjectMailSyncEnabled) {
                          handleSubjectSync();
                          return;
                        }
                        setToastMessage('Mail sync is not available right now.');
                        setToastType('warning');
                        setShowToast(true);
                      }}
                      disabled={emailSyncing || !emailSyncAvailable}
                      style={{
                        ...secondaryButtonStyle,
                        opacity: emailSyncing || !emailSyncAvailable ? 0.6 : 1,
                        cursor: emailSyncing || !emailSyncAvailable ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {emailSyncing ? 'Syncing…' : normalMailSyncAvailable ? 'Mail Sync' : 'Mail Sync Based on Subject'}
                    </button>
                    {unparsedResumeCount > 0 && (
                      <button
                        type="button"
                        onClick={handleReparseUnparsedResumes}
                        disabled={reparsingUnparsedResumes || unparsedResumeLoading}
                        title="Retry resumes that were downloaded but not parsed"
                        style={{
                          ...secondaryButtonStyle,
                          borderColor: isDark ? 'rgba(251,191,36,0.34)' : 'rgba(245,158,11,0.42)',
                          background: isDark ? 'rgba(120,53,15,0.22)' : '#FFFBEB',
                          color: isDark ? '#FDE68A' : '#92400E',
                          opacity: reparsingUnparsedResumes || unparsedResumeLoading ? 0.65 : 1,
                          cursor: reparsingUnparsedResumes || unparsedResumeLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {reparsingUnparsedResumes ? 'Re-parsing…' : `Re-parse (${unparsedResumeCount})`}
                      </button>
                    )}
                  </div>
                  {subjectMailSyncEnabled && (
                    <div style={{
                      flexBasis: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: 8,
                      marginTop: 8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: `1px solid ${isDark ? 'rgba(45,212,191,0.18)' : 'rgba(167,243,208,0.95)'}`,
                      background: isDark ? 'rgba(15,23,42,0.45)' : 'rgba(255,255,255,0.72)',
                    }}>
                      {[
                        ['Extracted mails', emailSyncTotalMails],
                        ['Resumes parsed', emailSyncResumeParsed],
                        ['Duplicate resumes', emailSyncDuplicates],
                      ].map(([label, value]) => (
                        <div key={label} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: isDark ? '#F8FAFC' : '#0F172A', lineHeight: 1.1 }}>
                            {Number(value || 0)}
                          </div>
                          <div style={{ fontSize: 10, color: isDark ? '#94A3B8' : '#64748B', marginTop: 2 }}>
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {emailSyncAvailable && (
              <div style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#64748B', marginTop: 10, lineHeight: 1.5, paddingTop: 10, borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}` }}>
                {emailSyncState.startedAt
                  ? `Started ${formatLastSync(emailSyncState.startedAt)}`
                  : emailLastSyncAt
                    ? `Last sync ${formatLastSync(emailLastSyncAt)}`
                    : 'No email sync activity yet.'}
                {emailSyncing && emailLastUpdatedAt ? ` • Updated ${formatLastSync(emailLastUpdatedAt)}` : ''}
                {!emailSyncing && emailSyncCompletedAt ? ` • Completed ${formatLastSync(emailSyncCompletedAt)}` : ''}
              </div>
            )}
              </>
            )}
          </div>
        )}

        {(showSourcingLogs || sourcingActivityHasAnyCard) && sourcingActivityHasAnyCard && (
          <div style={{
            ...sectionSurface,
            padding: '10px 12px',
          }}>
            <button
              type="button"
              onClick={() => setSourcingActivityCollapsed((prev) => !prev)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: 'wrap',
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, ...textPrimary, margin: 0 }}>Sourcing Activity</h3>
                    <span style={{ fontSize: 11, ...textMuted }}>
                      {sourcingActivityIsProcessing ? 'Processing' : 'Latest status'} · {sourcingActivityTotalUpdates} update{sourcingActivityTotalUpdates === 1 ? '' : 's'}
                    </span>
                  </div>
                  {sourcingLastUpdatedLabel && (
                    <span style={{ fontSize: 11, ...textMuted, whiteSpace: 'nowrap' }}>
                      Last updated {sourcingLastUpdatedLabel}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {sourcingStatusChips.map((chip) => (
                    <span
                      key={chip.key}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 600,
                        color: chip.tone === 'active' ? '#1D4ED8' : chip.tone === 'done' ? '#15803D' : (isDark ? '#CBD5E1' : '#64748B'),
                        background: chip.tone === 'active'
                          ? (isDark ? 'rgba(59,130,246,0.16)' : 'rgba(219,234,254,0.95)')
                          : chip.tone === 'done'
                          ? (isDark ? 'rgba(34,197,94,0.16)' : 'rgba(220,252,231,0.95)')
                          : (isDark ? 'rgba(51,65,85,0.72)' : 'rgba(241,245,249,0.95)'),
                        border: `1px solid ${chip.tone === 'active'
                          ? (isDark ? 'rgba(96,165,250,0.28)' : 'rgba(147,197,253,0.9)')
                          : chip.tone === 'done'
                          ? (isDark ? 'rgba(74,222,128,0.22)' : 'rgba(134,239,172,0.9)')
                          : (isDark ? 'rgba(100,116,139,0.4)' : 'rgba(226,232,240,0.95)')}`,
                      }}
                    >
                      <span>{chip.label}</span>
                      <span style={{ opacity: 0.8 }}>{chip.detail}</span>
                    </span>
                  ))}
                </div>
                {!sourcingActivityCollapsed && sourcingLatestSummary && (
                  <div style={{ fontSize: 12, ...textMuted, marginTop: 10, lineHeight: 1.45 }}>
                    {sourcingLatestSummary}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: isDark ? '#E2E8F0' : '#334155',
                  borderRadius: 8,
                  padding: '7px 11px',
                  border: `1px solid ${isDark ? '#374151' : '#D7DFEA'}`,
                  background: isDark ? '#111827' : '#fff',
                  flexShrink: 0,
                }}
              >
                {sourcingActivityCollapsed ? 'View details' : 'Hide details'}
              </span>
            </button>

            {!sourcingActivityCollapsed && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
                  gap: 10,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}`,
                }}
              >
                {sourcingActivityCards.map((process) => (
                  <ProcessStatusCard
                    key={process.key}
                    title={process.title}
                    icon={<span style={{ fontSize: 14, fontWeight: 800 }}>{process.icon}</span>}
                    metric={process.metric}
                    logs={process.logs}
                    processing={process.processing}
                    statusOverride={process.statusOverride}
                    completedLabel={process.completedLabel || 'Completed'}
                    idleLabel={process.idleLabel || 'Queued'}
                    emptySummary={process.emptySummary || 'This source has not started yet.'}
                    isDark={isDark}
                    onViewLogs={() => setActiveSourcingLog(process.key)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Outreach action buttons ── */}
        {canStartOutreach && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleStartOutreachForSelected}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: '9px 0', borderRadius: 10, border: `1.5px solid ${primaryColor}`, color: primaryColor, background: 'transparent', cursor: 'pointer' }}
            >
              Outreach Selected{selectedCandidates.length > 0 ? ` (${selectedCandidates.length})` : ''}
            </button>
            <button
              onClick={handleStartOutreachForAll}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: '9px 0', borderRadius: 10, border: `1.5px solid ${primaryColor}`, color: '#fff', background: primaryColor, cursor: 'pointer' }}
            >
              Outreach All ({totalProfiles})
            </button>
          </div>
        )}

        {/* ── Search + Filter bar ── */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#6B7280' : '#9CA3AF'} strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name, title, skills…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '8px 32px 8px 34px', borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: isDark ? '#111827' : '#F9FAFB', color: isDark ? '#F9FAFB' : '#111827', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#6B7280' : '#9CA3AF'} strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 12px', borderRadius: 8, border: `1px solid ${activeFilterCount > 0 ? primaryColor : (isDark ? '#374151' : '#E5E7EB')}`, background: activeFilterCount > 0 ? hexToRgba(primaryColor, 0.08) : 'transparent', color: activeFilterCount > 0 ? primaryColor : (isDark ? '#D1D5DB' : '#374151'), cursor: 'pointer' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h5M20 20v-5h-5M4.22 14a8 8 0 0014.56 0M19.78 10a8 8 0 00-14.56 0"/></svg>
              Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>
            {filterDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: isDark ? '#111827' : '#fff', border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, borderRadius: 12, boxShadow: '0 14px 30px rgba(15,23,42,0.18)', zIndex: 100, minWidth: 230, padding: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#9CA3AF' : '#6B7280', padding: '4px 6px' }}>Source</div>
                {SOURCE_FILTER_OPTIONS.map(source => (
                  <div key={source.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', cursor: 'pointer', borderRadius: 8 }} onClick={() => {
                    setSourceFilter(prev => prev.includes(source.value) ? prev.filter(x => x !== source.value) : [...prev, source.value]);
                  }}>
                    <div style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${sourceFilter.includes(source.value) ? primaryColor : '#D1D5DB'}`, background: sourceFilter.includes(source.value) ? primaryColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {sourceFilter.includes(source.value) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
                    </div>
                    <SourceBadge source={source.label} />
                    <span style={{ fontSize: 12, color: isDark ? '#E5E7EB' : '#374151' }}>{source.label}</span>
                  </div>
                ))}

                <div style={{ borderTop: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}`, marginTop: 8, paddingTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#9CA3AF' : '#6B7280', padding: '4px 6px' }}>Candidate status</div>
                  {STATUS_FILTER_OPTIONS.map(status => (
                    <div key={status.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', cursor: 'pointer', borderRadius: 8 }} onClick={() => {
                      setStatusFilter(prev => prev.includes(status.value) ? prev.filter(x => x !== status.value) : [...prev, status.value]);
                    }}>
                      <div style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${statusFilter.includes(status.value) ? primaryColor : '#D1D5DB'}`, background: statusFilter.includes(status.value) ? primaryColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {statusFilter.includes(status.value) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
                      </div>
                      <span style={{ fontSize: 12, color: isDark ? '#E5E7EB' : '#374151' }}>{status.label}</span>
                    </div>
                  ))}
                </div>

                {(sourceFilter.length > 0 || statusFilter.length > 0) && (
                  <div style={{ borderTop: `1px solid ${isDark ? '#1F2937' : '#E5E7EB'}`, marginTop: 8, paddingTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', cursor: 'pointer', borderRadius: 8, color: '#DC2626', fontSize: 12 }} onClick={() => { setSourceFilter([]); setStatusFilter([]); setFilterDropdownOpen(false); }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      Clear filters
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── List View Layout (like Master Database) ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', background: sourcingListTheme.surface, borderRadius: 14, border: `1px solid ${isDark ? '#1F2937' : '#EEF2F7'}` }}>
          {/* Select All Checkbox */}
          {!serverLoading && paginatedCandidates.length > 0 && (
            <div style={{ padding: '8px 14px', borderBottom: `1px solid ${sourcingListTheme.separator}`, marginBottom: 8 }}>
              <div
                onClick={handleSelectAll}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', width: 'fit-content' }}
              >
                <div style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${selectedCandidates.length > 0 && selectedCandidates.length === paginatedCandidates.length ? primaryColor : sourcingListTheme.controlBorder}`, background: selectedCandidates.length > 0 && selectedCandidates.length === paginatedCandidates.length ? primaryColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {selectedCandidates.length > 0 && selectedCandidates.length === paginatedCandidates.length && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
                </div>
                <span style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#6B7280' }}>
                  {selectedCandidates.length > 0 && selectedCandidates.length === paginatedCandidates.length ? 'Deselect All' : 'Select All'}
                </span>
              </div>
            </div>
          )}

          {serverLoading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', ...textMuted, fontSize: 13 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }}>
                <path d="M4 4v5h5M20 20v-5h-5M4.22 14a8 8 0 0014.56 0M19.78 10a8 8 0 00-14.56 0"/>
              </svg>
              <br />Loading candidates…
            </div>
          ) : paginatedCandidates.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 500, ...textPrimary, marginBottom: 4 }}>No candidates found</div>
              <div style={{ fontSize: 13, ...textMuted }}>Try adjusting your search or filter.</div>
            </div>
          ) : (
            paginatedCandidates.map((candidate, idx) => {
              const id = candidate.id || candidate.candidate_id || `candidate_${idx}`;
              const { candidateId, name, initials, title, company, location, email, matchScore, experience, normalizedSkills, foundOnLabels, funnelStage } = extractCandidate(candidate, idx);
              const statusKey = getCandidateStatusKey(candidate);
              const statusLabel = STATUS_FILTER_OPTIONS.find((option) => option.value === statusKey)?.label || funnelStage || 'Sourced';
              
              const avatarColors = [
                { bg: isDark ? 'rgba(96,165,250,0.16)' : '#E6F1FB', color: isDark ? '#93C5FD' : '#185FA5' },
                { bg: isDark ? 'rgba(45,212,191,0.15)' : '#E1F5EE', color: isDark ? '#5EEAD4' : '#0F6E56' },
                { bg: isDark ? 'rgba(251,191,36,0.15)' : '#FAEEDA', color: isDark ? '#FCD34D' : '#854F0B' },
                { bg: isDark ? 'rgba(129,140,248,0.16)' : '#EEEDFE', color: isDark ? '#A5B4FC' : '#534AB7' },
                { bg: isDark ? 'rgba(248,113,113,0.14)' : '#FAECE7', color: isDark ? '#FCA5A5' : '#993C1D' }
              ];
              const av = avatarColors[idx % avatarColors.length];

              const statusBadgeColors = {
                identified: { bg: isDark ? 'rgba(148,163,184,0.14)' : '#F1EFE8', color: isDark ? '#CBD5E1' : '#5F5E5A' },
                contacted: { bg: isDark ? 'rgba(45,212,191,0.15)' : '#E1F5EE', color: isDark ? '#5EEAD4' : '#0F6E56' },
                interested: { bg: isDark ? 'rgba(251,191,36,0.15)' : '#FAEEDA', color: isDark ? '#FCD34D' : '#854F0B' },
                shortlisted: { bg: isDark ? 'rgba(129,140,248,0.16)' : '#EEEDFE', color: isDark ? '#A5B4FC' : '#534AB7' },
                interviewed: { bg: isDark ? 'rgba(96,165,250,0.16)' : '#DBEAFE', color: isDark ? '#93C5FD' : '#1D4ED8' },
                scheduled: { bg: isDark ? 'rgba(56,189,248,0.15)' : '#E0F2FE', color: isDark ? '#7DD3FC' : '#0369A1' },
                follow_up: { bg: isDark ? 'rgba(244,114,182,0.15)' : '#FCE7F3', color: isDark ? '#F9A8D4' : '#BE185D' },
                declined: { bg: isDark ? 'rgba(248,113,113,0.14)' : '#FEE2E2', color: isDark ? '#FCA5A5' : '#B91C1C' },
                hired: { bg: isDark ? 'rgba(34,197,94,0.14)' : '#DCFCE7', color: isDark ? '#86EFAC' : '#15803D' },
              };
              const badge = statusBadgeColors[statusKey] || statusBadgeColors.identified;

              return (
                <React.Fragment key={id}>
                  {idx > 0 && <div style={{ height: '0.5px', background: sourcingListTheme.separator, margin: '0 12px' }} />}
                  
                  <div
                    onClick={() => { handleViewProfile(candidate); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      borderRadius: 12,
                      marginBottom: 8,
                      border: selectedCandidates.includes(id) ? `1px solid ${primaryColor}` : `1px solid ${sourcingListTheme.border}`,
                      cursor: 'pointer',
                      background: selectedCandidates.includes(id) ? sourcingListTheme.selected : sourcingListTheme.card,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => { if (!selectedCandidates.includes(id)) { e.currentTarget.style.background = sourcingListTheme.hover; e.currentTarget.style.borderColor = sourcingListTheme.hoverBorder; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = isDark ? '0 10px 24px rgba(2, 6, 23, 0.32)' : '0 4px 12px rgba(79, 70, 229, 0.1)'; } }}
                    onMouseLeave={e => { if (!selectedCandidates.includes(id)) { e.currentTarget.style.background = sourcingListTheme.card; e.currentTarget.style.borderColor = sourcingListTheme.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; } }}
                  >
                    <div
                      onClick={e => { e.stopPropagation(); handleSelectCandidate(id); }}
                      style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${selectedCandidates.includes(id) ? primaryColor : sourcingListTheme.controlBorder}`, background: selectedCandidates.includes(id) ? primaryColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
                    >
                      {selectedCandidates.includes(id) && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>}
                    </div>

                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: av.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: av.color, fontSize: 14, fontWeight: 500, flexShrink: 0 }}>
                      {initials}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#F8FAFC' : '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {name}
                        </div>
                        {foundOnLabels.length > 0 && (
                          <div title={`Sourced from ${foundOnLabels.join(', ')}`} style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            {foundOnLabels.slice(0, 3).map(s => <SourceBadge key={s} source={s} />)}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {email && email !== 'N/A' && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: sourcingListTheme.emailBg, color: sourcingListTheme.emailText, border: `1px solid ${sourcingListTheme.emailBorder}`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>
                            ✉️ {email}
                          </span>
                        )}
                        {location !== 'N/A' && (
                          <span
                            title={location}
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 12,
                              background: sourcingListTheme.locationBg,
                              color: sourcingListTheme.locationText,
                              border: `1px solid ${sourcingListTheme.locationBorder}`,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '180px',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                          >
                            📍 {location}
                          </span>
                        )}
                        {experience !== 'N/A' && (
                          <span
                            title={String(experience)}
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 12,
                              background: sourcingListTheme.expBg,
                              color: sourcingListTheme.expText,
                              border: `1px solid ${sourcingListTheme.expBorder}`,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '160px',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                          >
                            {experience}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ padding: '3px 10px', borderRadius: '100px', fontSize: 11, fontWeight: 500, letterSpacing: '0.02em', flexShrink: 0, background: badge.bg, color: badge.color }}>
                      {statusLabel}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* ── Pagination ── */}
        {displayedTotalRecords > ITEMS_PER_PAGE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, ...textMuted }}>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, displayedTotalRecords)} of {displayedTotalRecords}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ fontSize: 13, padding: '6px 16px', borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: 'transparent', color: currentPage === 1 ? (isDark ? '#4B5563' : '#D1D5DB') : (isDark ? '#F9FAFB' : '#111827'), cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
              >Prev</button>
              <span style={{ fontSize: 13, ...textMuted, minWidth: 80, textAlign: 'center' }}>Page {currentPage} of {totalCandidatePages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalCandidatePages, p + 1))}
                disabled={currentPage === totalCandidatePages}
                style={{ fontSize: 13, padding: '6px 16px', borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`, background: 'transparent', color: currentPage === totalCandidatePages ? (isDark ? '#4B5563' : '#D1D5DB') : (isDark ? '#F9FAFB' : '#111827'), cursor: currentPage === totalCandidatePages ? 'not-allowed' : 'pointer', opacity: currentPage === totalCandidatePages ? 0.5 : 1 }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      <Toast message={toastMessage} type={toastType} visible={showToast} onClose={() => setShowToast(false)} duration={4000} />

      <ProcessLogsModal
        open={activeSourcingLog === 'google'}
        title="Online Sourcing Log"
        logs={fileLogsState.google.fetched && fileLogsState.google.logs.length > 0
          ? fileLogsState.google.logs.map((e) => ({ message: e.message || '', timestamp: e.timestamp || '', logType: e.log_type }))
          : googleLogs}
        isDark={isDark}
        onClose={() => setActiveSourcingLog(null)}
      >
        {fileLogsState.google.loading && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#94A3B8' : '#64748B' }}>Loading full log…</div>}
        {fileLogsState.google.error && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#FCA5A5' : '#DC2626' }}>{fileLogsState.google.error}</div>}
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeSourcingLog === 'linkedin'}
        title="LinkedIn Sourcing Log"
        logs={fileLogsState.linkedin.fetched && fileLogsState.linkedin.logs.length > 0
          ? fileLogsState.linkedin.logs.map((e) => ({ message: e.message || '', timestamp: e.timestamp || '', logType: e.log_type }))
          : linkedinLogs}
        isDark={isDark}
        onClose={() => setActiveSourcingLog(null)}
      >
        {fileLogsState.linkedin.loading && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#94A3B8' : '#64748B' }}>Loading full log…</div>}
        {fileLogsState.linkedin.error && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#FCA5A5' : '#DC2626' }}>{fileLogsState.linkedin.error}</div>}
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeSourcingLog === 'resumebox'}
        title={`${RESUME_LIBRARY_LABEL} Log`}
        logs={fileLogsState.resumebox.fetched && fileLogsState.resumebox.logs.length > 0
          ? fileLogsState.resumebox.logs.map((e) => ({ message: e.message || '', timestamp: e.timestamp || '', logType: e.log_type }))
          : resumeBoxLogs}
        isDark={isDark}
        onClose={() => setActiveSourcingLog(null)}
      >
        {fileLogsState.resumebox.loading && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#94A3B8' : '#64748B' }}>Loading full log…</div>}
        {fileLogsState.resumebox.error && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#FCA5A5' : '#DC2626' }}>{fileLogsState.resumebox.error}</div>}
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeSourcingLog === 'email-sync'}
        title="Mail Sync Based on Subject Log"
        logs={emailSyncFullLogs}
        isDark={isDark}
        onClose={() => setActiveSourcingLog(null)}
      >
        {fileLogsState['email-sync']?.loading && <div style={{ padding: '8px 0', fontSize: 12, color: isDark ? '#94A3B8' : '#64748B' }}>Loading full log…</div>}
      </ProcessLogsModal>

      <JDScoreConfigModal
        open={scoreConfigOpen}
        jdId={effectiveJdId}
        jobTitle={jobTitle}
        isDark={isDark}
        primaryColor={primaryColor}
        onClose={() => setScoreConfigOpen(false)}
        onSaved={handleRefreshCandidates}
      />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default CandidateResults;
